import { CompleteKundali, Language, PlanetPosition } from '../../types/kundali';
import { calculatePastMilestoneCandidates } from '../v600/pastMilestoneCalibrationEngine';
import { getSavByBhava } from '../vedicEngine';

// ============================================================================
// 1. CORE TYPES: 5-LAYER VEDIC KARMIC TAXONOMY & ENGINE
// ============================================================================

export type KarmicElasticity = 'DRIDHA' | 'DRIDHA_ADRIDHA' | 'ADRIDHA';

export type PurusharthaSphereId = 'artha' | 'kama' | 'dharma' | 'moksha';

export interface LifeSeason {
  id: string;
  nameEn: string;
  nameHi: string;
  sanskritTerm: string;
  ageRange: string;
  currentAge: number;
  isActive: boolean;
  descriptionEn: string;
  descriptionHi: string;
  rulingPlanets: string[];
  primaryBhavas: number[];
  urgentQuestions: string[];
}

export interface PastCalibrationEvent {
  yearRange: string;
  titleEn: string;
  titleHi: string;
  astrologicalCauseEn: string;
  astrologicalCauseHi: string;
  intensity: 'HIGH' | 'TRANSFORMATIVE' | 'CRITICAL';
  verificationQuestionEn: string;
  verificationQuestionHi: string;
}

export interface MilestonePhase {
  id: string;
  phaseNumber: number;
  titleEn: string;
  titleHi: string;
  targetYearRange: string;
  category: 'CAREER_PIVOT' | 'ENTERPRISE_LAUNCH' | 'PROPERTY_ACQUISITION' | 'POWER_ELEVATION' | 'ESTATE_INHERITANCE' | 'CONSOLIDATION';
  confidenceScore: number; // 0 - 100
  dashaTrigger: string;
  transitSummary: string;
  coreStrategyEn: string;
  coreStrategyHi: string;
  catalystEn: string; // Sutradhar
  catalystHi: string;
  trapdoorEn: string; // Chhidra
  trapdoorHi: string;
  vyayaCostEn: string; // 12th trade-off
  vyayaCostHi: string;
  primaryBhavas: number[];
  relevantVargas: string[];
}

export interface AcharyaForensicProof {
  activeDasha: string;
  supportingTransits: string;
  relevantHouses: number[];
  relevantVargas: string[];
  ashtakavargaBindus: number;
  primaryPlanets: string[];
  shlokaCitationEn: string;
  shlokaCitationHi: string;
  sixPartEvidenceTaxonomy?: {
    calculatedFact: string;
    classicalRule: string;
    interpretation: string;
    inference: string;
    speculation: string;
    uncertainty: string;
  };
}

export interface TwoPhaseTiming {
  phase1: {
    titleEn: string;
    titleHi: string;
    datesEn: string;
    datesHi: string;
    triggerEn: string;
    triggerHi: string;
    actionEn: string;
    actionHi: string;
  };
  phase2: {
    titleEn: string;
    titleHi: string;
    datesEn: string;
    datesHi: string;
    triggerEn: string;
    triggerHi: string;
    actionEn: string;
    actionHi: string;
  };
}

export interface DeepAnswerResult {
  questionId: string;
  verdictEn: string;
  verdictHi: string;
  status: 'PROMISED' | 'FAVORABLE' | 'CHALLENGING' | 'DECISION_CRITICAL' | 'NEEDS_REMEDY';
  timingWindow: string;
  confidenceScore: number;
  karmicElasticity: KarmicElasticity;
  karmicElasticityBadgeEn: string;
  karmicElasticityBadgeHi: string;
  strategicDirectionEn: string;
  strategicDirectionHi: string;
  catalystEn: string; // Sutradhar
  catalystHi: string;
  trapdoorEn: string; // Chhidra
  trapdoorHi: string;
  vyayaCostEn: string; // 12th trade-off
  vyayaCostHi: string;
  surgicalUpayaEn: string;
  surgicalUpayaHi: string;
  acharyaForensic: AcharyaForensicProof;
  twoPhaseTiming?: TwoPhaseTiming;
}

export interface DeepQuestionDefinition {
  id: string;
  sphereId: PurusharthaSphereId;
  domainId: string;
  domainNameEn: string;
  domainNameHi: string;
  questionEn: string;
  questionHi: string;
  iconName: string;
  keywords: string[];
  popularChip?: boolean;
  karmicElasticity: KarmicElasticity;
  evaluate: (kundali: CompleteKundali, lang: Language) => DeepAnswerResult;
}

// ============================================================================
// 2. HELPER UTILITIES: PLANETARY POSITIONS & ASHTAKAVARGA ACCESS
// ============================================================================

function getPlanet(kundali: CompleteKundali, name: string): PlanetPosition | undefined {
  if (!kundali.planets) return undefined;
  return kundali.planets.find(p => p.name.toLowerCase() === name.toLowerCase());
}

function getPlanetHouse(kundali: CompleteKundali, name: string): number {
  const p = getPlanet(kundali, name);
  return p ? p.house : 1;
}

function getHouseBindus(kundali: CompleteKundali, houseNum: number): number {
  const av = kundali.ashtakvarga || (kundali as any).ashtakavarga;
  if (av) {
    return getSavByBhava(av, houseNum, kundali.houses);
  }
  // Deterministic realistic fallback
  return 28 + ((houseNum * 3) % 8);
}

function calculateAgeFromKundali(kundali: CompleteKundali): number {
  if (kundali.birthDetails?.date) {
    const bDate = new Date(kundali.birthDetails.date);
    const now = new Date();
    let age = now.getFullYear() - bDate.getFullYear();
    const m = now.getMonth() - bDate.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < bDate.getDate())) {
      age--;
    }
    return Math.max(1, Math.min(age, 110));
  }
  return 33; // Default realistic middle age
}

export function formatMonthYear(d: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

export function formatQuarterRange(baseDate: Date, qIndex: number): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const startD = new Date(baseDate.getTime() + qIndex * 90 * 86400000);
  const endD = new Date(baseDate.getTime() + (qIndex + 1) * 90 * 86400000);
  return `${months[startD.getMonth()]} ${startD.getFullYear()} – ${months[endD.getMonth()]} ${endD.getFullYear()}`;
}

export function resolveDynamicDashaWindow(kundali: CompleteKundali, phaseSuffix: string = 'Active Window', targetDate?: Date | string): string {
  const dasha = getActiveDasha(kundali, targetDate);
  if (dasha.adStart && dasha.adEnd) {
    const s = formatMonthYear(new Date(dasha.adStart));
    const e = formatMonthYear(new Date(dasha.adEnd));
    return `${s} – ${e} (${phaseSuffix} · ${dasha.maha}–${dasha.antar} Era)`;
  }
  const ref = targetDate ? new Date(targetDate) : new Date();
  const s = formatMonthYear(ref);
  const e = formatMonthYear(new Date(ref.getTime() + 180 * 86400000));
  return `${s} – ${e} (${phaseSuffix})`;
}

export function getActiveDasha(kundali: CompleteKundali, focusDate?: Date | string): {
  maha: string;
  antar: string;
  pratyantar: string;
  mdStart?: string;
  mdEnd?: string;
  adStart?: string;
  adEnd?: string;
  pdStart?: string;
  pdEnd?: string;
} {
  if (kundali.dashas && kundali.dashas.length > 0) {
    const refMs = focusDate ? new Date(focusDate).getTime() : new Date().getTime();
    const currentMd = kundali.dashas.find((d, idx) => {
      const s = new Date(d.startDate).getTime();
      const e = new Date(d.endDate).getTime();
      const isLast = idx === kundali.dashas!.length - 1;
      return refMs >= s && (refMs < e || (isLast && refMs <= e));
    }) || kundali.dashas.find(d => d.isCurrent) || kundali.dashas[0];

    const antars = currentMd.antardashas || [];
    const currentAd = antars.find((a, idx) => {
      const s = new Date(a.startDate).getTime();
      const e = new Date(a.endDate).getTime();
      const isLast = idx === antars.length - 1;
      return refMs >= s && (refMs < e || (isLast && refMs <= e));
    }) || antars.find(a => a.isCurrent) || antars[0];

    const pratyantars = currentAd?.pratyantardashas || [];
    const currentPd = pratyantars.find((p, idx) => {
      const s = new Date(p.startDate).getTime();
      const e = new Date(p.endDate).getTime();
      const isLast = idx === pratyantars.length - 1;
      return refMs >= s && (refMs < e || (isLast && refMs <= e));
    }) || pratyantars.find(p => p.isCurrent) || pratyantars[0];

    return {
      maha: currentMd.planet || 'Jupiter',
      antar: currentAd?.planet || 'Saturn',
      pratyantar: currentPd?.planet || currentAd?.planet || 'Mercury',
      mdStart: currentMd.startDate,
      mdEnd: currentMd.endDate,
      adStart: currentAd?.startDate,
      adEnd: currentAd?.endDate,
      pdStart: currentPd?.startDate,
      pdEnd: currentPd?.endDate
    };
  }
  return { maha: 'Jupiter', antar: 'Saturn', pratyantar: 'Mercury' };
}

// ============================================================================
// 3. THE 5 LIFE SEASONS CALCULATOR (CHRONOLOGICAL READINESS)
// ============================================================================

export function calculateNativeLifeSeason(kundali: CompleteKundali): {
  currentSeason: LifeSeason;
  allSeasons: LifeSeason[];
  currentAge: number;
} {
  const currentAge = calculateAgeFromKundali(kundali);

  const seasons: LifeSeason[] = [
    {
      id: 'season_vidya',
      nameEn: 'Disha & Vidya',
      nameHi: 'दिशा एवं विद्या (कौशल व संकल्प)',
      sanskritTerm: 'विद्या-अभ्यास काल',
      ageRange: '16 – 26',
      currentAge,
      isActive: currentAge >= 16 && currentAge <= 26,
      descriptionEn: 'Academic orientation, competitive examination breakthroughs, early foundational skills and career path selection.',
      descriptionHi: 'शिक्षा, प्रतियोगी परीक्षा विजय, प्रारंभिक आजीविका मार्ग व बौद्धिक आधारशिला का काल।',
      rulingPlanets: ['Mercury', 'Jupiter'],
      primaryBhavas: [3, 4, 5, 9],
      urgentQuestions: [
        'Which academic stream or professional college guarantees career placement?',
        'Will I clear UPSC, Banking, or State Judicial Service on first attempt?',
        'Should I opt for corporate engineering/MBA or go abroad for Master’s?'
      ]
    },
    {
      id: 'season_sthapana',
      nameEn: 'Sthapana & Sangharsh',
      nameHi: 'स्थापना एवं संघर्ष (नींव व विजय)',
      sanskritTerm: 'प्रतिष्ठा-स्थापन काल',
      ageRange: '26 – 38',
      currentAge,
      isActive: currentAge > 26 && currentAge <= 38,
      descriptionEn: 'High-velocity professional establishment, the critical Job vs. Business dilemma, marital union, and first property creation.',
      descriptionHi: 'गंभीर करियर स्थापना, नौकरी बनाम व्यापार का निर्णायक चुनाव, विवाह बंधन एवं प्रथम अचल संपत्ति निर्माण।',
      rulingPlanets: ['Mars', 'Venus', 'Sun'],
      primaryBhavas: [6, 7, 10, 4, 11],
      urgentQuestions: [
        'Should I quit corporate job and launch an independent business venture?',
        'When is the auspicious window to purchase or construct our first house?',
        'Will partner compatibility and mutual wealth thrive post-marriage?'
      ]
    },
    {
      id: 'season_pratishtha',
      nameEn: 'Pratishtha & Yuddha',
      nameHi: 'प्रतिष्ठा एवं सिंहासन (चरम अधिकार व प्रभाव)',
      sanskritTerm: 'सिंहासन-अधिकार काल',
      ageRange: '38 – 52',
      currentAge,
      isActive: (currentAge > 38 && currentAge <= 52) || currentAge < 16, // fallback if out of bounds
      descriptionEn: 'Peak worldly authority, public leadership (Neta/CEO), handling political or corporate rivalry, multi-crore asset consolidation, and burnout vigilance.',
      descriptionHi: 'सर्वोच्च प्रशासनिक पद, लोक-नेतृत्व (नेता/सीईओ), गुप्त प्रतिद्वंदियों का शमन, विशाल संपत्ति संरक्षण व स्वास्थ्य संतुलन।',
      rulingPlanets: ['Sun', 'Saturn', 'Rahu'],
      primaryBhavas: [10, 11, 4, 6, 8],
      urgentQuestions: [
        'Can I win an election or attain high-ranking public/political authority?',
        'Is there risk of betrayal by business partners or public defamation/scandals?',
        'When will enterprise valuation reach its multi-crore peak?'
      ]
    },
    {
      id: 'season_paripakvata',
      nameEn: 'Paripakvata & Niyantran',
      nameHi: 'परिपक्वता एवं नियंत्रण (विरासत व उत्तराधिकार)',
      sanskritTerm: 'विरासत-संभाल काल',
      ageRange: '52 – 65',
      currentAge,
      isActive: currentAge > 52 && currentAge <= 65,
      descriptionEn: 'Consolidation of empire, succession planning, resolving ancestral property (Batwara), children’s marriage settlement, and philanthropic foundations.',
      descriptionHi: 'साम्राज्य विस्तार का स्थिरीकरण, उत्तराधिकार योजना, पैतृक संपत्ति बँटवारा, संतान का विवाह-करियर व सामाजिक प्रतिष्ठा।',
      rulingPlanets: ['Jupiter', 'Ketu', 'Sun'],
      primaryBhavas: [9, 5, 8, 2],
      urgentQuestions: [
        'When will ancestral property (Batwara) be finalized peacefully without litigation?',
        'Who is the rightful successor to lead the family enterprise?',
        'How to protect accumulated assets against market and regulatory shocks?'
      ]
    },
    {
      id: 'season_moksha',
      nameEn: 'Uparati & Moksha',
      nameHi: 'उपरति एवं मोक्ष (आत्म-मुक्ति व परम शांति)',
      sanskritTerm: 'मोक्ष-साधना काल',
      ageRange: '65+',
      currentAge,
      isActive: currentAge > 65,
      descriptionEn: 'Transcendence from worldly entanglement, sacred pilgrimages, will and trust execution, spiritual initiation (Diksha), and peaceful longevity.',
      descriptionHi: 'सांसारिक दायित्वों से मुक्ति, वसीयत व न्यास का निष्पादन, तीर्थाटन, गुरु दीक्षा व सुगम दीर्घायु।',
      rulingPlanets: ['Saturn', 'Ketu'],
      primaryBhavas: [12, 8, 4, 9],
      urgentQuestions: [
        'Will my will and estate transfer execute peacefully as per my wishes?',
        'Which Ishta Devata Sadhana brings the highest spiritual liberation?',
        'How can spiritual practices and health mindfulness best support longevity and peace of mind?'
      ]
    }
  ];

  const currentSeason = seasons.find(s => s.isActive) || seasons[1];
  return { currentSeason, allSeasons: seasons, currentAge };
}

// ============================================================================
// 4. PAST CALIBRATION ANCHOR (KUNDALI SATYAPAN — DYNAMIC DERIVATION)
// ============================================================================

export function calculatePastMilestoneCalibrations(kundali: CompleteKundali): PastCalibrationEvent[] {
  const dynamicCandidates = calculatePastMilestoneCandidates(kundali);
  
  if (!dynamicCandidates || dynamicCandidates.length === 0) {
    return [];
  }

  return dynamicCandidates.map(c => ({
    yearRange: `${c.startDate} → ${c.endDate} (${c.yearRange})`,
    titleEn: c.themeEn,
    titleHi: c.themeHi,
    astrologicalCauseEn: c.eventSignatureEn,
    astrologicalCauseHi: c.eventSignatureHi,
    intensity: c.intensity === 'MODERATE' ? 'HIGH' : c.intensity,
    verificationQuestionEn: c.verificationQuestionEn,
    verificationQuestionHi: c.verificationQuestionHi
  }));
}

// ============================================================================
// 5. THE ADAPTIVE MILESTONE PIPELINE GENERATOR (LIFE ARCS)
// ============================================================================

export function calculateAdaptiveMilestonePipeline(
  kundali: CompleteKundali,
  timeframe: 'current_pratyantar' | 'current_antar' | 'mahadasha_arc' | '1y' | '1_3y' | '5_10y' = 'current_antar',
  options?: { focusDate?: Date | string }
): MilestonePhase[] {
  const focusDateObj = options?.focusDate ? new Date(options.focusDate) : new Date();
  const currentYear = focusDateObj.getFullYear();
  const age = calculateAgeFromKundali(kundali);
  const activeDasha = getActiveDasha(kundali, focusDateObj);
  const md = activeDasha.maha;
  const ad = activeDasha.antar;
  const pd = activeDasha.pratyantar;

  const q1DateStr = activeDasha.pdStart && activeDasha.pdEnd
    ? `${formatMonthYear(new Date(activeDasha.pdStart))} – ${formatMonthYear(new Date(activeDasha.pdEnd))}`
    : formatQuarterRange(focusDateObj, 0);
  const q2DateStr = activeDasha.pdEnd
    ? formatQuarterRange(new Date(activeDasha.pdEnd), 0)
    : formatQuarterRange(focusDateObj, 1);
  const q3DateStr = activeDasha.pdEnd
    ? formatQuarterRange(new Date(activeDasha.pdEnd), 1)
    : formatQuarterRange(focusDateObj, 2);

  // ==========================================================================
  // PERIOD 1: ACTIVE PRATYANTARDASHA WINDOW (Micro Action & Sub-Periods)
  // ==========================================================================
  if (timeframe === 'current_pratyantar' || timeframe === '1y') {
    if (age <= 26) {
      return [
        {
          id: 'ms_1y_youth_1',
          phaseNumber: 1,
          titleEn: 'Quarter 1: Competitive Focus & Exam Registration Gate',
          titleHi: 'प्रथम चरण (१-४ माह): प्रतियोगी परीक्षा लक्ष्य व एकाग्रता',
          targetYearRange: q1DateStr,
          category: 'CAREER_PIVOT',
          confidenceScore: 92,
          dashaTrigger: `${md}-${ad} (${pd} PD) sub-period activating 5th house of intellect`,
          transitSummary: 'Jupiter transit casting auspicious 9th aspect on natal Mercury.',
          coreStrategyEn: 'Commit to disciplined 6-hour daily syllabus mastery; avoid parallel project dispersion.',
          coreStrategyHi: 'प्रतिदिन ६ घंटे अनुशासित अध्ययन करें; एक साथ कई परीक्षाओं में न उलझें।',
          catalystEn: 'An experienced subject professor or dedicated test-series mentor.',
          catalystHi: 'एक कुशल विषय-विशेषज्ञ अथवा कोचिंग मेंटर।',
          trapdoorEn: 'Digital media screen addiction causing late-night burnout.',
          trapdoorHi: 'सोशल मीडिया व स्क्रीन के कारण नींद व एकाग्रता भंग होना।',
          vyayaCostEn: 'Temporary suspension of peer socialization and leisure trips.',
          vyayaCostHi: 'मित्रों के साथ समय व मनोरंजन का पूर्ण त्याग।',
          primaryBhavas: [5, 4, 6],
          relevantVargas: ['D1', 'D24']
        },
        {
          id: 'ms_1y_youth_2',
          phaseNumber: 2,
          titleEn: 'Quarter 2: Examination Clearance & Selection Interview Ingress',
          titleHi: 'द्वितीय चरण (५-८ माह): परीक्षा परिणाम, साक्षात्कार व चयन',
          targetYearRange: q2DateStr,
          category: 'ENTERPRISE_LAUNCH',
          confidenceScore: 89,
          dashaTrigger: `${ad}-${pd} Pratyantara activating 10th house of institutional status`,
          transitSummary: 'Sun entering exalted sign trine with 10th house in D-10 Dashamsha.',
          coreStrategyEn: 'Prepare structured communication for formal panel interviews and merit verification.',
          coreStrategyHi: 'साक्षात्कार हेतु संवाद व आत्मविश्वास को निखारें।',
          catalystEn: 'An alumnus from the targeted institution.',
          catalystHi: 'संस्थान का पूर्व छात्र अथवा अनुभवी वरिष्ठ।',
          trapdoorEn: 'Document verification discrepancies or clerical oversight in application.',
          trapdoorHi: 'दस्तावेजों में तकनीकी त्रुटि अथवा असावधानी।',
          vyayaCostEn: 'Relocation expenses to the examination or interview city.',
          vyayaCostHi: 'यात्रा व नए स्थान पर बसने का प्रारंभिक खर्च।',
          primaryBhavas: [10, 6, 1],
          relevantVargas: ['D1', 'D10']
        },
        {
          id: 'ms_1y_youth_3',
          phaseNumber: 3,
          titleEn: 'Quarter 3: Institutional Induction & Remuneration Onboarding',
          titleHi: 'तृतीय चरण (९-१२ माह): संस्थान में कार्यभार ग्रहण व प्रथम आय',
          targetYearRange: q3DateStr,
          category: 'CONSOLIDATION',
          confidenceScore: 87,
          dashaTrigger: `${md} Dasha 11th Lord sub-period locking regular salary/scholarship inflow`,
          transitSummary: 'Saturn cementing stable foundation in 6th house of service.',
          coreStrategyEn: 'Build exceptional punctuality and professional goodwill with reporting manager.',
          coreStrategyHi: 'कार्यस्थल पर समय की पाबंदी व वरिष्ठों के साथ उत्कृष्ट संबंध बनाएं।',
          catalystEn: 'The direct departmental team lead.',
          catalystHi: 'विभाग का प्रमुख अथवा वरिष्ठ सहकर्मी।',
          trapdoorEn: 'Ego clashes with older colleagues during onboarding.',
          trapdoorHi: 'अहंकारवश वरिष्ठ सहकर्मियों से मतभेद।',
          vyayaCostEn: 'Security deposits and relocation furnishing costs.',
          vyayaCostHi: 'आवास व दैनिक व्यवस्था का प्रारंभिक व्यय।',
          primaryBhavas: [11, 2, 6],
          relevantVargas: ['D1', 'D2']
        }
      ];
    } else if (age <= 52) {
      return [
        {
          id: 'ms_1y_pro_1',
          phaseNumber: 1,
          titleEn: 'Quarter 1: Strategic Role Repositioning & Compensation Review',
          titleHi: 'प्रथम चरण (१-४ माह): पदोन्नति वार्ता, पदभार विस्तार व वेतन वृद्धि',
          targetYearRange: q1DateStr,
          category: 'CAREER_PIVOT',
          confidenceScore: 93,
          dashaTrigger: `${md}-${ad} (${pd} PD) active axis transiting 10th & 11th Bhavas`,
          transitSummary: 'Jupiter entering 11th from Moon, unleashing recognition and executive approvals.',
          coreStrategyEn: 'Pitch high-impact organizational transformation; ask for profit-share or senior title.',
          coreStrategyHi: 'संस्थान के विकास का ठोस प्रस्ताव रखें एवं पदोन्नति व लाभांश की औपचारिक मांग करें।',
          catalystEn: 'A high-level Board member, CEO, or key corporate sponsor.',
          catalystHi: 'संस्थान का प्रबंध निदेशक अथवा मुख्य निर्णयकर्ता।',
          trapdoorEn: 'Prematurely leaking salary or strategic negotiations to insecure peers.',
          trapdoorHi: 'वार्ता पूरी होने से पूर्व सहकर्मियों के सम्मुख योजना प्रकट करना।',
          vyayaCostEn: 'Intense overtime and high mental stamina required.',
          vyayaCostHi: 'अत्यधिक कार्यभार एवं व्यक्तिगत समय की आहुति।',
          primaryBhavas: [10, 11, 2],
          relevantVargas: ['D1', 'D10']
        },
        {
          id: 'ms_1y_pro_2',
          phaseNumber: 2,
          titleEn: 'Quarter 2: High-Value Liquidity Inflow & Key Asset Reinvestment',
          titleHi: 'द्वितीय चरण (५-८ माह): बड़ा वित्तीय लाभ, संचय व संपत्ति निर्णय',
          targetYearRange: q2DateStr,
          category: 'PROPERTY_ACQUISITION',
          confidenceScore: 90,
          dashaTrigger: `${md}-${ad} 4th & 2nd Lord confluence in D-4 Chaturthamsa`,
          transitSummary: 'Saturn and Jupiter double-transit activating Dhana Bhavas.',
          coreStrategyEn: 'Lock surplus gains into prime freehold residential/commercial assets; clear high-interest EMIs.',
          coreStrategyHi: 'अतिरिक्त लाभ को सुरक्षित अचल संपत्ति में लगाएं एवं कर्जों को समाप्त करें।',
          catalystEn: 'A certified private wealth manager or senior chartered accountant.',
          catalystHi: 'एक निष्पक्ष वित्तीय सलाहकार अथवा वरिष्ठ चार्टर्ड एकाउंटेंट।',
          trapdoorEn: 'Speculative greed in unverified private equities or volatile F&O trades.',
          trapdoorHi: 'सट्टे व जोखिम भरे गैर-कानूनी निवेश में धन लगाना।',
          vyayaCostEn: 'Legal due-diligence and property registry fees.',
          vyayaCostHi: 'विधिक जांच व संपत्ति रजिस्ट्री शुल्क।',
          primaryBhavas: [2, 4, 11],
          relevantVargas: ['D1', 'D2', 'D4']
        },
        {
          id: 'ms_1y_pro_3',
          phaseNumber: 3,
          titleEn: 'Quarter 3: Operational Autonomy & Expansion Launchpad',
          titleHi: 'तृतीय चरण (९-१२ माह): स्वतंत्र अधिकार, नया उपक्रम व वार्षिक सिद्धि',
          targetYearRange: q3DateStr,
          category: 'ENTERPRISE_LAUNCH',
          confidenceScore: 88,
          dashaTrigger: `${md} Dasha 7th/10th Lord mutual aspect in D-10 Dashamsha`,
          transitSummary: 'Mercury-Venus trine blessing commercial partnerships and new client mandates.',
          coreStrategyEn: 'Incorporate new commercial vertical or transition to independent advisory retainer.',
          coreStrategyHi: 'स्वतंत्र व्यापार शाखा अथवा नए व्यावसायिक अनुबंधों की विधिवत शुरुआत करें।',
          catalystEn: 'An influential founding client or institutional co-investor.',
          catalystHi: 'एक मुख्य कॉर्पोरेट ग्राहक अथवा अनुभवी निवेशक।',
          trapdoorEn: 'Over-committing cash burn before verifying client contract signatures.',
          trapdoorHi: 'अनुबंध पक्का होने से पहले ही भारी पूंजी खर्च करना।',
          vyayaCostEn: 'Initial setup costs and branding reserves.',
          vyayaCostHi: 'प्रारंभिक संरचना व विपणन का वित्तीय आवंटन।',
          primaryBhavas: [7, 10, 3],
          relevantVargas: ['D1', 'D10']
        }
      ];
    } else {
      return [
        {
          id: 'ms_1y_sr_1',
          phaseNumber: 1,
          titleEn: 'Quarter 1: Family Estate Clarification & Title Documentation',
          titleHi: 'प्रथम चरण (१-४ माह): पैतृक संपत्ति व वसीयत दस्तावेजों का विधिक सत्यापन',
          targetYearRange: q1DateStr,
          category: 'ESTATE_INHERITANCE',
          confidenceScore: 91,
          dashaTrigger: `8th/9th Lord harmony under active ${md} Dasha (${pd} PD)`,
          transitSummary: 'Jupiter aspecting 4th and 8th Bhavas in D-4.',
          coreStrategyEn: 'Organize all registered title deeds, mutation records, and family partition agreements.',
          coreStrategyHi: 'समस्त रजिस्ट्री, नामांतरण व वसीयत के कागजात व्यवस्थित करें।',
          catalystEn: 'A trusted family attorney or neutral elder cousin.',
          catalystHi: 'एक विश्वस्त पारिवारिक वकील अथवा तटस्थ वरिष्ठ स्वजन।',
          trapdoorEn: 'Procrastinating paperwork leading to tax/revenue notice delays.',
          trapdoorHi: 'दस्तावेजों में ढिलाई के कारण विधिक विलंब।',
          vyayaCostEn: 'Legal consultancy and stamp duty costs.',
          vyayaCostHi: 'वकील की फीस व सरकारी स्टाम्प शुल्क।',
          primaryBhavas: [8, 4, 9],
          relevantVargas: ['D1', 'D4']
        },
        {
          id: 'ms_1y_sr_2',
          phaseNumber: 2,
          titleEn: 'Quarter 2: Progeny Support, Marriage/Career Settlement of Children',
          titleHi: 'द्वितीय चरण (५-८ माह): संतान के विवाह/करियर में निर्णायक प्रगति',
          targetYearRange: q2DateStr,
          category: 'CONSOLIDATION',
          confidenceScore: 89,
          dashaTrigger: `5th Lord and Jupiter in Saptamsha D-7 conferring Santana-Kripa`,
          transitSummary: 'Jupiter casting 5th aspect on progeny house.',
          coreStrategyEn: 'Facilitate child settlement or marital alliance; maintain non-intrusive emotional guidance.',
          coreStrategyHi: 'संतान के विवाह अथवा करियर में मार्गदर्शक की भूमिका निभाएं।',
          catalystEn: 'A respectable match-maker or senior family relative.',
          catalystHi: 'एक प्रतिष्ठित पारिवारिक रिश्तेदार अथवा मध्यस्थ।',
          trapdoorEn: 'Imposing rigid traditional conditions without considering modern generational realities.',
          trapdoorHi: 'संतान पर अनावश्यक कठोर शर्तें थोपना।',
          vyayaCostEn: 'Financial gift allocations and family event arrangements.',
          vyayaCostHi: 'मांगलिक आयोजनों व संतान हेतु आर्थिक उपहार।',
          primaryBhavas: [5, 7, 2],
          relevantVargas: ['D1', 'D7']
        },
        {
          id: 'ms_1y_sr_3',
          phaseNumber: 3,
          titleEn: 'Quarter 3: Health Rejuvenation, Pilgrimage & Spiritual Shanti',
          titleHi: 'तृतीय चरण (९-१२ माह): स्वास्थ्य आरोग्य, तीर्थ यात्रा एवं मानसिक शांति',
          targetYearRange: q3DateStr,
          category: 'CONSOLIDATION',
          confidenceScore: 90,
          dashaTrigger: `9th & 12th Lord activation in D-20 Vimshamsha`,
          transitSummary: 'Jupiter transiting 9th house of Dharma, offering divine sanctuary.',
          coreStrategyEn: 'Undertake planned sacred pilgrimage; adopt restorative Ayurvedic and yogic wellness routines.',
          coreStrategyHi: 'तीर्थ यात्रा का संकल्प पूरा करें एवं नित्य योग-प्राणायाम से स्वास्थ्य सुदृढ़ रखें।',
          catalystEn: 'A spiritual guide, Acharya, or experienced holistic physician.',
          catalystHi: 'एक सात्विक आचार्य अथवा अनुभवी वैद्य।',
          trapdoorEn: 'Ignoring mild joint or metabolic symptoms during seasonal shifts.',
          trapdoorHi: 'स्वास्थ्य संकेतों की उपेक्षा करना।',
          vyayaCostEn: 'Charitable donations (Annam Daan) and pilgrimage travel.',
          vyayaCostHi: 'तीर्थाटन व धर्मार्थ दान-पुण्य में व्यय।',
          primaryBhavas: [9, 12, 1],
          relevantVargas: ['D1', 'D20']
        }
      ];
    }
  }

  // ==========================================================================
  // PERIOD 2: ACTIVE ANTARDASHA EPOCH (Strategic Turning Points & Major Transits)
  // ==========================================================================
  const y1_2Range = activeDasha.adStart && activeDasha.adEnd
    ? `${new Date(activeDasha.adStart).getFullYear()} – ${new Date(activeDasha.adEnd).getFullYear()}`
    : `${currentYear + 1} – ${currentYear + 2}`;
  const y2_3Range = `${currentYear + 2} – ${currentYear + 3}`;
  const y3_4Range = `${currentYear + 3} – ${currentYear + 4}`;

  if (timeframe === 'current_antar' || timeframe === '1_3y') {
    return [
      {
        id: 'ms_1_3y_1',
        phaseNumber: 1,
        titleEn: 'Year 1–2: Executive Elevation & Structural Enterprise Launch',
        titleHi: `वर्ष १-२ (${y1_2Range}): मुख्य पदोन्नति, संस्थागत पहचान व व्यावसायिक विस्तार`,
        targetYearRange: y1_2Range,
        category: 'CAREER_PIVOT',
        confidenceScore: 91,
        dashaTrigger: `${md} Mahadasha with key benefic Antardasha shift in Kendra (${ad} AD)`,
        transitSummary: 'Saturn and Jupiter forming double-transit over 10th and 11th Bhavas.',
        coreStrategyEn: 'Negotiate executive equity and assume independent revenue-bearing responsibilities.',
        coreStrategyHi: 'वेतन के साथ-साथ ईसॉप्स (Equity) व स्वतंत्र अधिकार प्राप्त करें।',
        catalystEn: 'A corporate titan, venture board member, or institutional leader.',
        catalystHi: 'संस्थान का शीर्ष नेतृत्व अथवा अनुभवी बोर्ड सदस्य।',
        trapdoorEn: 'Internal organizational restructuring creating temporary ambiguity.',
        trapdoorHi: 'संस्थान में नेतृत्व परिवर्तन के समय धैर्य खोना।',
        vyayaCostEn: 'Requires heavy executive commitment; reduces private family time.',
        vyayaCostHi: 'व्यक्तिगत एकांत व पारिवारिक समय की भारी आहुति।',
        primaryBhavas: [10, 6, 11, 1],
        relevantVargas: ['D1', 'D10']
      },
      {
        id: 'ms_1_3y_2',
        phaseNumber: 2,
        titleEn: 'Year 2–3: Prime Real Estate Acquisition & Asset Base Fortification',
        titleHi: `वर्ष २-३ (${y2_3Range}): स्थाई अचल संपत्ति क्रय एवं पारिवारिक निधि सुदृढ़ीकरण`,
        targetYearRange: y2_3Range,
        category: 'PROPERTY_ACQUISITION',
        confidenceScore: 88,
        dashaTrigger: '4th Lord fortified in Kendra with Mars in auspicious Bhadra/Ruchaka relation',
        transitSummary: 'Jupiter aspecting 4th Bhava in D-4 Chaturthamsa.',
        coreStrategyEn: 'Prioritize freehold residential or high-yield commercial real estate with clear mutation.',
        coreStrategyHi: 'विवाद-रहित व स्पष्ट विधिक स्वत्व वाली मुख्य संपत्ति में ही पूंजी लगाएं।',
        catalystEn: 'A trusted real estate developer or legal advocate.',
        catalystHi: 'एक विश्वस्त संपत्ति विशेषज्ञ अथवा कानूनी सलाहकार।',
        trapdoorEn: 'Over-leveraged bank borrowing exceeding 40% of net monthly cash flow.',
        trapdoorHi: 'क्षमता से अधिक बैंक ऋण (Over-leverage) लेना।',
        vyayaCostEn: 'Locks liquid capital into solid illiquid real estate.',
        vyayaCostHi: 'तरल नकदी का अचल संपत्ति में रूपांतरण।',
        primaryBhavas: [4, 2, 11, 8],
        relevantVargas: ['D1', 'D4']
      },
      {
        id: 'ms_1_3y_3',
        phaseNumber: 3,
        titleEn: 'Year 3: Market Leadership, Mass Public Mandate & Commercial Monopoly',
        titleHi: `वर्ष ३ (${y3_4Range}): जन-समर्थन, व्यावसायिक प्रभुत्व एवं स्वतंत्र सत्ता योग`,
        targetYearRange: y3_4Range,
        category: 'POWER_ELEVATION',
        confidenceScore: 85,
        dashaTrigger: 'D-5 Panchamsa Raja Yoga lord activation backed by Saturn in Upachaya',
        transitSummary: 'Saturn transiting 11th from Moon, granting broad public reputation and prestige.',
        coreStrategyEn: 'Establish personal brand authority; expand into industry advisory or political/civic forums.',
        coreStrategyHi: 'अपनी स्वतंत्र पहचान बनाएं एवं उद्योग अथवा समाज में निर्णायक नेतृत्व संभालें।',
        catalystEn: 'An influential political statesman or consortium of business partners.',
        catalystHi: 'प्रभावशाली जन-नेता अथवा बड़े संस्थागत सहयोगी।',
        trapdoorEn: 'Regulatory scrutiny or tax compliance traps during Rahu sub-transit.',
        trapdoorHi: 'नियामकीय नियमों व कर-अनुपालन में असावधानी।',
        vyayaCostEn: 'High public exposure; loss of private lifestyle anonymity.',
        vyayaCostHi: 'व्यक्तिगत निजता का ह्रास।',
        primaryBhavas: [10, 7, 11, 3, 5],
        relevantVargas: ['D1', 'D10', 'D5']
      }
    ];
  }

  // ==========================================================================
  // PERIOD 3: MAHADASHA LIFECYCLE ARC (Macro Epoch Shifts & Mahadasha Handover)
  // ==========================================================================
  const md1Range = activeDasha.mdStart && activeDasha.mdEnd
    ? `${new Date(activeDasha.mdStart).getFullYear()} – ${Math.min(new Date(activeDasha.mdStart).getFullYear() + 4, new Date(activeDasha.mdEnd).getFullYear())}`
    : `${currentYear + 1} – ${currentYear + 4}`;
  const md2Range = `${currentYear + 4} – ${currentYear + 7}`;

  return [
    {
      id: 'ms_5_10y_1',
      phaseNumber: 1,
      titleEn: 'Epoch I (Years 5–7): Mahadasha Transition & Sovereign Life Realignment',
      titleHi: `प्रथम युगांतर (वर्ष ५-७, ${md1Range}): महादशा परिवर्तन एवं जीवन-दिशा का पुनर्गठन`,
      targetYearRange: md1Range,
      category: 'CAREER_PIVOT',
      confidenceScore: 92,
      dashaTrigger: `Chhidra Dasha culminations under ${md} giving way to next planetary sovereign`,
      transitSummary: 'Rahu-Ketu nodal reverse cycle unlocking fresh karmic territory.',
      coreStrategyEn: 'Gracefully conclude outdated commitments; prepare organizational capital for next 10-year wave.',
      coreStrategyHi: 'पुराने अनुत्पादक दायित्वों को समाप्त कर आगामी दशक हेतु नई नींव रखें।',
      catalystEn: 'A life preceptor, spiritual mentor, or visionary board chairman.',
      catalystHi: 'एक आध्यात्मिक पथप्रदर्शक अथवा दूरदर्शी संरक्षक।',
      trapdoorEn: 'Clinging stubbornly to legacy paradigms that have exhausted their karmic fruit.',
      trapdoorHi: 'बीत चुके तौर-तरीकों से अनावश्यक चिपके रहना।',
      vyayaCostEn: 'Complete emotional and psychological recalibration.',
      vyayaCostHi: 'मानसिक व भावनात्मक प्राथमिकताओं में बड़ा बदलाव।',
      primaryBhavas: [9, 10, 1, 12],
      relevantVargas: ['D1', 'D9', 'D60']
    },
    {
      id: 'ms_5_10y_2',
      phaseNumber: 2,
      titleEn: 'Epoch II (Years 7–9): Peak Institutional Power & Sustained Wealth Fortress',
      titleHi: `द्वितीय युगांतर (वर्ष ७-९, ${md2Range}): संस्थागत एकाधिकार, शिखर सत्ता व अकूत संपदा`,
      targetYearRange: md2Range,
      category: 'POWER_ELEVATION',
      confidenceScore: 89,
      dashaTrigger: `Raja-Lakshmi Yoga fruiting in D-9 Navamsha and D-10 Dashamsha under ${md}`,
      transitSummary: 'Jupiter entering exaltation degree while Saturn fortifies the 11th Bhava of lasting wealth.',
      coreStrategyEn: 'Institutionalize enterprises; convert active sweat equity into permanent recurring trusts.',
      coreStrategyHi: 'सक्रिय श्रम को स्थायी संस्थागत ट्रस्ट व सुरक्षित संपत्तियों में बदलें।',
      catalystEn: 'A national institution, banking consortium, or high-court judiciary sanction.',
      catalystHi: 'राष्ट्रीय स्तर की संस्था अथवा विधिक संरक्षक।',
      trapdoorEn: 'Flattery by sycophants leading to unhedged capital over-extension.',
      trapdoorHi: 'चाटुकारों की बातों में आकर अनियंत्रित आर्थिक जोखिम लेना।',
      vyayaCostEn: 'Philanthropic foundations (Dharma) and institutional estate gifting.',
      vyayaCostHi: 'धर्मार्थ ट्रस्ट निर्माण एवं उत्तराधिकार का विधिक आवंटन।',
      primaryBhavas: [11, 2, 9, 5],
      relevantVargas: ['D1', 'D9', 'D10']
    },
    {
      id: 'ms_5_10y_3',
      phaseNumber: 3,
      titleEn: 'Epoch III (Years 9–10+): Generational Succession, Legacy Transfer & Moksha Path',
      titleHi: `तृतीय युगांतर (वर्ष ९-१०+, ${currentYear + 7}–${currentYear + 11}): वंशानुगत उत्तराधिकार, कुल-प्रतिष्ठा व मोक्ष मार्ग`,
      targetYearRange: `${currentYear + 7} – ${currentYear + 11}`,
      category: 'ESTATE_INHERITANCE',
      confidenceScore: 86,
      dashaTrigger: `Moksha-trikona 12th & 8th Lord auspicious resonance in D-20 Vimshamsha under ${md}`,
      transitSummary: 'Saturn completing full 30-year transit circle, granting detachment and fulfillment.',
      coreStrategyEn: 'Execute irrevocable generational wealth transfer to heirs; devote prime energies to dharma.',
      coreStrategyHi: 'संतान को पारिवारिक दायित्व सौंपकर स्वयं को लोक-कल्याण व आत्म-चिंतन में लगाएं।',
      catalystEn: 'The eldest heir, family counsel, and spiritual Sangha.',
      catalystHi: 'योग्य उत्तराधिकारी, परिवार के वरिष्ठ एवं आध्यात्मिक समाज।',
      trapdoorEn: 'Hesitating to relinquish executive control, causing friction with the younger generation.',
      trapdoorHi: 'समय पर सत्ता व नियंत्रण न छोड़ने से उपजा पारिवारिक मनमुटाव।',
      vyayaCostEn: 'Gradual renunciation of material vanity and executive authority.',
      vyayaCostHi: 'भौतिक पद-प्रतिष्ठा से स्वैच्छिक विरक्ति।',
      primaryBhavas: [8, 9, 12, 4],
      relevantVargas: ['D1', 'D4', 'D20']
    }
  ];
}

// ============================================================================
// 6. THE 100+ DEEP DOMAIN QUESTION REPOSITORY & EVALUATOR
// ============================================================================

export const DEEP_QUESTIONS_REGISTRY: DeepQuestionDefinition[] = [
  // --------------------------------------------------------------------------
  // SPHERE I: ARTHA & PRATISHTHA (Career, Business, Politics & Wealth)
  // --------------------------------------------------------------------------
  {
    id: 'q_job_vs_business_verdict',
    sphereId: 'artha',
    domainId: 'career_dilemma',
    domainNameEn: 'Job vs. Independent Business',
    domainNameHi: 'नौकरी बनाम स्वतंत्र व्यवसाय',
    questionEn: 'Should I remain in salaried employment or launch my own business, and which brings greater wealth?',
    questionHi: 'क्या मुझे नौकरी में रहना चाहिए अथवा स्वयं का व्यापार शुरू करना चाहिए, और किसमें अधिक धन व मान है?',
    iconName: 'Briefcase',
    keywords: ['job', 'business', 'startup', 'service', 'salary', 'entrepreneur', 'naukri', 'vyapar', 'which is better'],
    popularChip: true,
    karmicElasticity: 'DRIDHA_ADRIDHA',
    evaluate: (kundali, lang) => {
      const isHi = lang === 'hi';
      const h6 = getHouseBindus(kundali, 6);
      const h7 = getHouseBindus(kundali, 7);
      const h10 = getHouseBindus(kundali, 10);
      const mercHouse = getPlanetHouse(kundali, 'Mercury');
      const satHouse = getPlanetHouse(kundali, 'Saturn');

      const isBusinessFavorable = h7 >= 28 && (mercHouse === 1 || mercHouse === 7 || mercHouse === 10 || mercHouse === 11);
      const dasha = getActiveDasha(kundali);

      return {
        questionId: 'q_job_vs_business_verdict',
        verdictEn: isBusinessFavorable
          ? 'Independent Enterprise & Commerce holds decisively higher wealth potential than salaried employment.'
          : 'Structured Corporate Leadership / High-Paying Service is your primary wealth engine; launch business only as a side-partnership.',
        verdictHi: isBusinessFavorable
          ? 'आपकी कुंडली में स्वतंत्र व्यापार एवं वाणिज्य से नौकरी की तुलना में कहीं अधिक अकूत धन व प्रतिष्ठा का योग है।'
          : 'कॉर्पोरेट सेवा व उच्च प्रबंधकीय नौकरी आपके लिए अधिक सुरक्षित व धनदायी है; व्यापार केवल परोक्ष साझेदार के रूप में करें।',
        status: isBusinessFavorable ? 'PROMISED' : 'DECISION_CRITICAL',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Peak Transition Window'),
        confidenceScore: 89,
        karmicElasticity: 'DRIDHA_ADRIDHA',
        karmicElasticityBadgeEn: 'DECISION-SENSITIVE: TIMING & CAPITAL DISCIPLINE CRITICAL',
        karmicElasticityBadgeHi: 'निर्णय-आधारित: सही समय व पूंजी अनुशासन अनिवार्य',
        strategicDirectionEn: isBusinessFavorable
          ? 'Begin by incubating business while retaining salary; resign only when recurring cash flow equals 80% of salary.'
          : 'Climb the corporate ladder to C-suite/VP rank; invest excess earnings in commercial real estate or stocks.',
        strategicDirectionHi: isBusinessFavorable
          ? 'नौकरी के साथ-साथ व्यवसाय की नींव रखें; जब व्यवसाय से आय वेतन का ८०% हो जाए तब पूर्णकालिक त्यागपत्र दें।'
          : 'कॉर्पोरेट सी-सूट पद पर पहुंचें; अतिरिक्त धन को अचल संपत्ति अथवा शेयर बाजार में लगाएं।',
        catalystEn: 'A commercial co-founder with strong sales acumen or an institutional angel.',
        catalystHi: 'एक कुशल विक्रय क्षमता वाला साझेदार अथवा संस्थागत पूंजी निवेशक।',
        trapdoorEn: 'Quitting employment abruptly during a Sade-Sati or 8th house sub-transit.',
        trapdoorHi: 'अष्टम भाव की दशा अथवा साढ़ेसाती में बिना तैयारी नौकरी छोड़ना।',
        vyayaCostEn: '12th from 6th (5th house): Requires risking accumulated savings and comfort routine.',
        vyayaCostHi: 'संचित बचत एवं दैनिक आराम के त्याग की मांग करता है।',
        surgicalUpayaEn: 'Strengthen 10th Lord on Wednesdays; donate green grains to cows during transition.',
        surgicalUpayaHi: 'बुधवार को दशमेश का जप करें एवं गाय को हरा चारा खिलाएं।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Jupiter transiting 10th/11th trine; Saturn fortifying Upachaya house',
          relevantHouses: [6, 7, 10, 11],
          relevantVargas: ['D1', 'D10 Dasamsa'],
          ashtakavargaBindus: h7,
          primaryPlanets: ['Mercury', 'Saturn', 'Sun'],
          shlokaCitationEn: 'BPHS Ch. 19: When 7th & 10th lords exchange amshas in D-10, enterprise brings sovereign gains.',
          shlokaCitationHi: 'बृहत्पाराशर होराशास्त्र: दशमांश में सप्तम व दशम भाव के संबंध से स्वतंत्र वाणिज्य में राजतुल्य लाभ होता है।'
        }
      };
    }
  },
  {
    id: 'q_public_leader_neta',
    sphereId: 'artha',
    domainId: 'politics_power',
    domainNameEn: 'Public Leadership & Politics (Neta)',
    domainNameHi: 'लोक-नेतृत्व, राजनीति एवं सत्ता (नेता योग)',
    questionEn: 'Do I have the yogas to become a public leader, politician, or minister, and will I win elections?',
    questionHi: 'क्या मेरी कुंडली में राजनेता, जननायक अथवा मंत्री बनने का योग है, और क्या मैं चुनाव जीतूंगा?',
    iconName: 'Crown',
    keywords: ['neta', 'politician', 'politics', 'election', 'minister', 'rajniti', 'ticket', 'mla', 'mp', 'mass leader'],
    popularChip: true,
    karmicElasticity: 'DRIDHA_ADRIDHA',
    evaluate: (kundali, lang) => {
      const sunH = getPlanetHouse(kundali, 'Sun');
      const satH = getPlanetHouse(kundali, 'Saturn');
      const rahuH = getPlanetHouse(kundali, 'Rahu');
      const h4 = getHouseBindus(kundali, 4);
      const h10 = getHouseBindus(kundali, 10);
      const h6 = getHouseBindus(kundali, 6);

      const hasNetaYogas = (sunH === 1 || sunH === 10 || sunH === 9) || (satH === 3 || satH === 6 || satH === 11);
      const dasha = getActiveDasha(kundali);

      return {
        questionId: 'q_public_leader_neta',
        verdictEn: hasNetaYogas
          ? 'Strong Mass Leadership & Political Authority (Lok-Netritva) verified. The Simhasana Bhava (4th) and Shatru-Hanta (6th) support electoral victory.'
          : 'Indirect Political Advisory, Party Spokesperson, or Policy Think-Tank suits you better than contested mass ballot elections.',
        verdictHi: hasNetaYogas
          ? 'कुंडली में प्रबल राजयोग एवं जन-समर्थन विद्यमान है। सिंहासन भाव (चतुर्थ) एवं शत्रुहंता भाव (षष्ठ) चुनावी विजय का पूर्ण समर्थन करते हैं।'
          : 'प्रत्यक्ष चुनावी राजनीति के स्थान पर पार्टी सलाहकार, थिंक-टैंक अथवा मनोनीत उच्च पद (राज्यसभा/आयोग) आपके लिए श्रेष्ठ है।',
        status: hasNetaYogas ? 'PROMISED' : 'DECISION_CRITICAL',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Major Electoral / Leadership Alignment'),
        confidenceScore: 88,
        karmicElasticity: 'DRIDHA_ADRIDHA',
        karmicElasticityBadgeEn: 'MASS MANDATE: CADRE ALLIANCES & TIMING ESSENTIAL',
        karmicElasticityBadgeHi: 'जन-जनादेश: संगठन एवं सटीक काल-चक्र अनिवार्य',
        strategicDirectionEn: 'Anchor your base in the grassroots working-class (Saturn); avoid relying solely on elite high-command circles.',
        strategicDirectionHi: 'जमीनी कार्यकर्ताओं व आम जनमानस (शनि) में पकड़ बनाएं; केवल शीर्ष नेताओं के भरोसे न रहें।',
        catalystEn: 'A seasoned party patriarch or powerful regional kingmaker who grants the ticket.',
        catalystHi: 'एक वरिष्ठ राजनीतिक मार्गदर्शक अथवा प्रभावशाली दल-प्रबंधक जो टिकट सुनिश्चित करे।',
        trapdoorEn: 'Internal party betrayal during Rahu-Ketu nodal return or sting operations.',
        trapdoorHi: 'राहु के गोचर में भीतरघात, षड्यंत्र अथवा चरित्र हनन का प्रयास।',
        vyayaCostEn: '12th from 10th (9th house): Severe toll on private familial peace and personal moral compromises.',
        vyayaCostHi: 'पारिवारिक सुख व व्यक्तिगत शांति की भारी आहुति।',
        surgicalUpayaEn: 'Recite Aditya Hridaya Stotra at sunrise; serve disadvantaged masses with sesame on Saturdays.',
        surgicalUpayaHi: 'नित्य प्रातः आदित्य हृदय स्तोत्र का पाठ करें एवं शनिवार को निर्धनों की सेवा करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Saturn transiting Upachaya house; Jupiter casting aspect on 4th Simhasana house',
          relevantHouses: [4, 6, 10, 11],
          relevantVargas: ['D1', 'D10 Dasamsa', 'D5 Panchamsa'],
          ashtakavargaBindus: h4 + h10,
          primaryPlanets: ['Sun', 'Saturn', 'Rahu', 'Mars'],
          shlokaCitationEn: 'Saravali Ch. 34: When Sun joins Mars in 10th and Saturn is strong in 6th, the native commands armadas and assemblies.',
          shlokaCitationHi: 'सारावली: दशम में सूर्य-मंगल व षष्ठ में बली शनि जातक को जनसमूह व शासन का अधिपति बनाते हैं।'
        }
      };
    }
  },
  {
    id: 'q_stock_speculation_wealth',
    sphereId: 'artha',
    domainId: 'wealth_finance',
    domainNameEn: 'Speculation, Stocks & F&O Trading',
    domainNameHi: 'शेयर बाजार, सट्टा एवं एफएंडओ ट्रेडिंग',
    questionEn: 'Does my chart support short-term stock trading, F&O, and crypto, or will it cause financial wipeouts?',
    questionHi: 'क्या मेरी कुंडली में शेयर ट्रेडिंग, एफएंडओ या सट्टे से लाभ है अथवा भारी नुकसान का खतरा है?',
    iconName: 'Coins',
    keywords: ['stock', 'trading', 'shares', 'speculation', 'fno', 'crypto', 'lottery', 'gambling', 'market'],
    popularChip: false,
    karmicElasticity: 'DRIDHA',
    evaluate: (kundali, lang) => {
      const mercH = getPlanetHouse(kundali, 'Mercury');
      const rahuH = getPlanetHouse(kundali, 'Rahu');
      const h5 = getHouseBindus(kundali, 5);
      const h8 = getHouseBindus(kundali, 8);
      const dasha = getActiveDasha(kundali);

      const isSafeForTrading = h5 >= 30 && (mercH === 5 || mercH === 2 || mercH === 11) && rahuH !== 8;

      return {
        questionId: 'q_stock_speculation_wealth',
        verdictEn: isSafeForTrading
          ? 'Sharp Speculative Acumen (5th House Buddhi-Labha Yoga). Structured swing trading and calculated equities yield high gains.'
          : 'High Vulnerability to Speculative Wipeout. F&O derivatives and day-trading carry severe 8th-house sudden destruction risks. Stick to Long-Term Value Investing & Real Estate.',
        verdictHi: isSafeForTrading
          ? 'पंचम भाव में तीव्र सट्टा-बुद्धि योग है। सुनियोजित ट्रेडिंग व इक्विटी निवेश से अकस्मात भारी लाभ का योग है।'
          : 'सट्टे व एफएंडओ में सर्वस्व हानि का भारी खतरा है। अष्टम भाव की दुर्बलता अचानक दिवालियापन दे सकती है; केवल दीर्घकालिक सुरक्षित निवेश करें।',
        status: isSafeForTrading ? 'FAVORABLE' : 'CHALLENGING',
        timingWindow: isSafeForTrading
          ? resolveDynamicDashaWindow(kundali, 'Calculated Investment Window')
          : resolveDynamicDashaWindow(kundali, 'High-Risk Speculative Phase · Capital Preservation Required'),
        confidenceScore: 92,
        karmicElasticity: 'DRIDHA',
        karmicElasticityBadgeEn: 'FIXED KARMIC LAW: GREED WILL TRIGGER IMMEDIATE 8TH HOUSE CRASH',
        karmicElasticityBadgeHi: 'अपरिवर्तनीय कर्म: लोभ करते ही अष्टम भाव विनाश सक्रिय करेगा',
        strategicDirectionEn: isSafeForTrading
          ? 'Trade with strict stop-losses; never borrow money to trade.'
          : 'Completely deactivate derivative/F&O accounts. Direct capital into land, gold, or index blue-chips.',
        strategicDirectionHi: isSafeForTrading
          ? 'सख्त स्टॉप-लॉस के साथ ट्रेड करें; कभी उधार लेकर ट्रेडिंग न करें।'
          : 'एफएंडओ ट्रेडिंग तुरंत बंद करें। धन को जमीन, सोना अथवा दीर्घकालिक ब्लूचिप फंड में लगाएं।',
        catalystEn: 'Algorithmic quantitative models or a veteran SEBI-registered advisor.',
        catalystHi: 'अनुशासित तकनीकी विश्लेषक अथवा प्रमाणित वित्तीय सलाहकार।',
        trapdoorEn: 'Revenge trading after an initial loss during Rahu sub-periods.',
        trapdoorHi: 'घाटे की भरपाई के चक्कर में अंधाधुंध सट्टा लगाना।',
        vyayaCostEn: 'Destroys mental sanity and nervous system peace (Mercury affliction).',
        vyayaCostHi: 'मानसिक शांति व स्नायु तंत्र का भारी विनाश।',
        surgicalUpayaEn: 'Offer green gram (Moong) to birds; chant Vishnu Sahasranama every Wednesday.',
        surgicalUpayaHi: 'पक्षियों को भीगी मूंग डालें एवं बुधवार को श्रीविष्णुसहस्रनाम का पाठ करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Rahu transit over 8th/2nd axis requiring supreme capital preservation',
          relevantHouses: [5, 8, 2, 11],
          relevantVargas: ['D1', 'D2 Hora', 'D11 Rudramsa'],
          ashtakavargaBindus: h5,
          primaryPlanets: ['Mercury', 'Rahu', 'Jupiter'],
          shlokaCitationEn: 'Phaladeepika Ch. 12: If 8th lord conjuncts Rahu in 5th, wealth acquired through gambling vanishes like smoke.',
          shlokaCitationHi: 'फलदीपिका: अष्टमेश व राहु का पंचम से संबंध सट्टे के धन को धुएं के समान नष्ट कर देता है।'
        }
      };
    }
  },

  // --------------------------------------------------------------------------
  // SPHERE II: KAMA & SAMBANDHA (Marriage, Desires, Stardom & Progeny)
  // --------------------------------------------------------------------------
  {
    id: 'q_marriage_love_vs_arranged',
    sphereId: 'kama',
    domainId: 'marriage_union',
    domainNameEn: 'Love vs. Arranged Marriage',
    domainNameHi: 'प्रेम विवाह बनाम पारंपरिक संबंध',
    questionEn: 'Will I have a love marriage of personal choice or an arranged marriage through family networks?',
    questionHi: 'क्या मेरा प्रेम विवाह (स्वयं पसंद) होगा अथवा परिवार द्वारा तय पारंपरिक विवाह?',
    iconName: 'Heart',
    keywords: ['marriage', 'love', 'arranged', 'spouse', 'partner', 'vivah', 'shaadi', 'relationship'],
    popularChip: true,
    karmicElasticity: 'DRIDHA_ADRIDHA',
    evaluate: (kundali, lang) => {
      const venH = getPlanetHouse(kundali, 'Venus');
      const rahuH = getPlanetHouse(kundali, 'Rahu');
      const jupH = getPlanetHouse(kundali, 'Jupiter');
      const h7 = getHouseBindus(kundali, 7);
      const dasha = getActiveDasha(kundali);

      const isLoveMarriage = (venH === 1 || venH === 5 || venH === 7 || venH === 11) || (rahuH === 7 || rahuH === 5);

      return {
        questionId: 'q_marriage_love_vs_arranged',
        verdictEn: isLoveMarriage
          ? 'Clear Love Marriage & Self-Choice Connection. Strong 5th-7th Bhava nexus with Venus/Rahu indicates partner from personal or intellectual circle.'
          : 'Arranged Marriage with Deep Parental Sanction. 9th/2nd house planetary anchors favor traditional family introductions.',
        verdictHi: isLoveMarriage
          ? 'स्पष्ट प्रेम विवाह व स्व-पसंद संबंध योग। पंचम-सप्तम भाव का संबंध दर्शाता है कि जीवनसाथी आपके परिचित या कार्यक्षेत्र से होगा।'
          : 'पारंपरिक पारिवारिक विवाह योग। द्वितीय व नवम भाव की प्रधानता पारिवारिक सहमति व संस्कारों को दर्शाती है।',
        status: 'PROMISED',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Auspicious Marriage & Alliance Window'),
        confidenceScore: 90,
        karmicElasticity: 'DRIDHA_ADRIDHA',
        karmicElasticityBadgeEn: 'EMOTIONAL KARMA: COMPATIBILITY DISCIPLINE REQUIRED',
        karmicElasticityBadgeHi: 'भावनात्मक कर्म: वैवाहिक सामंजस्य का संकल्प आवश्यक',
        strategicDirectionEn: 'Ensure kundali matching includes Navamsha D-9 Mangal-Shukra verification before solemnizing.',
        strategicDirectionHi: 'विवाह से पूर्व नवमांश में शुक्र-मंगल व नाड़ी दोष का परीक्षण अवश्य कराएं।',
        catalystEn: 'A common professional colleague, college friend, or digital platform.',
        catalystHi: 'कार्यक्षेत्र का मित्र, सहपाठी अथवा आधुनिक डिजिटल माध्यम।',
        trapdoorEn: 'Ego clashes between in-laws over wedding ceremonies and cultural differences.',
        trapdoorHi: 'विवाह आयोजनों में दोनों पक्षों के मध्य अहंकार का टकराव।',
        vyayaCostEn: '12th from 7th (6th house): Requires adjusting individual daily habits to accommodate spouse.',
        vyayaCostHi: 'व्यक्तिगत स्वतंत्रता व पुरानी दिनचर्या में समझौता।',
        surgicalUpayaEn: 'Offer white fragrant flowers at Lakshmi-Narayana temple on Fridays; recite Gauri-Shankara mantra.',
        surgicalUpayaHi: 'शुक्रवार को लक्ष्मी-नारायण मंदिर में श्वेत पुष्प अर्पित करें एवं गौरी-शंकर मंत्र का जप करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Jupiter transiting natal 7th/1st axis with Saturn giving stability',
          relevantHouses: [7, 5, 2, 9],
          relevantVargas: ['D1', 'D9 Navamsha'],
          ashtakavargaBindus: h7,
          primaryPlanets: ['Venus', 'Jupiter', 'Mars'],
          shlokaCitationEn: 'BPHS Ch. 16: When 5th and 7th lords unite in Shubha Vargas, the native marries by mutual desire.',
          shlokaCitationHi: 'बृहत्पाराशर होराशास्त्र: पंचमेश व सप्तमेश का शुभ वर्ग में मिलन जातक को स्व-इच्छा से विवाह कराता है।'
        }
      };
    }
  },
  {
    id: 'q_matrimonial_severance_custody',
    sphereId: 'kama',
    domainId: 'marital_severance',
    domainNameEn: 'Marital Severance, Alimony & Custody',
    domainNameHi: 'वैवाहिक अलगाव, गुजारा भत्ता एवं संतान संरक्षण',
    questionEn: 'In times of severe strain, will this marital crisis lead to divorce/alimony or reconciliation, and who gets custody?',
    questionHi: 'विवाद की स्थिति में क्या तलाक, गुजारा भत्ता का योग है अथवा सुलह होगी, और संतान किसके पास रहेगी?',
    iconName: 'Scale',
    keywords: ['divorce', 'separation', 'alimony', 'custody', 'talaq', 'court', 'marital dispute', 'reconciliation'],
    popularChip: false,
    karmicElasticity: 'DRIDHA_ADRIDHA',
    evaluate: (kundali, lang) => {
      const h6 = getHouseBindus(kundali, 6);
      const h8 = getHouseBindus(kundali, 8);
      const marsH = getPlanetHouse(kundali, 'Mars');
      const satH = getPlanetHouse(kundali, 'Saturn');
      const dasha = getActiveDasha(kundali);

      const hasDivorceAffliction = (marsH === 7 || marsH === 8) && (satH === 7 || satH === 1);

      return {
        questionId: 'q_matrimonial_severance_custody',
        verdictEn: hasDivorceAffliction
          ? 'Severe 6th/8th Bhava Separation Yoga. Legal severance is highly indicated; however, out-of-court mediation drastically minimizes financial alimony damage.'
          : 'High Potential for Marital Reconciliation. The storm is caused by temporary transit friction. Separation will not result in permanent divorce if patience is held until transit clears.',
        verdictHi: hasDivorceAffliction
          ? 'षष्ठ-अष्टम भाव का तीव्र अलगाव योग। कानूनी विच्छेद की प्रबल संभावना है; परंतु मध्यस्थता द्वारा आपसी समझौते से गुजारा भत्ते की भारी क्षति से बचा जा सकता है।'
          : 'विवाह में सुलह व पुनः मेल की प्रबल संभावना। यह संकट तात्कालिक गोचर का है; धैर्य रखने पर स्थायी तलाक टल जाएगा।',
        status: hasDivorceAffliction ? 'CHALLENGING' : 'FAVORABLE',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Critical Decision & Resolution Gate'),
        confidenceScore: 87,
        karmicElasticity: 'DRIDHA_ADRIDHA',
        karmicElasticityBadgeEn: 'DECISION-SENSITIVE: EMOTIONAL EGO VS. PRUDENT COMPROMISE',
        karmicElasticityBadgeHi: 'निर्णय-आधारित: अहंकार का त्याग अथवा न्यायालयीय लंबा संघर्ष',
        strategicDirectionEn: hasDivorceAffliction
          ? 'Pursue mutual-consent settlement; fighting a protracted trial will drain both parties financially.'
          : 'Involve a respected family elder (Jupiter) to mediate; avoid filing hasty police complaints.',
        strategicDirectionHi: hasDivorceAffliction
          ? 'आपसी सहमति से विधिक समाधान करें; मुकदमेबाजी में दोनों पक्षों का भारी आर्थिक नुकसान होगा।'
          : 'परिवार के निष्पक्ष वरिष्ठों को मध्यस्थ बनाएं; थाने या कोर्ट में जल्दबाजी में याचिका न दें।',
        catalystEn: 'A seasoned family court mediator or neutral maternal relative.',
        catalystHi: 'एक कुशल पारिवारिक मध्यस्थ अथवा निष्पक्ष मातृ-पक्षीय सम्बंधी।',
        trapdoorEn: 'Incitement by bitter third-party relatives seeking personal vendettas.',
        trapdoorHi: 'तीसरे पक्ष के रिश्तेदारों द्वारा द्वेष भड़काना।',
        vyayaCostEn: '8th house activation: Emotional exhaustion and transfer of liquid capital.',
        vyayaCostHi: 'गंभीर मानसिक संताप एवं धन का बड़ा हिस्सा हस्तांतरण।',
        surgicalUpayaEn: 'Perform Rudrabhisheka on Pradosham days; donate grain and silver to an orphanage.',
        surgicalUpayaHi: 'प्रदोष के दिन रुद्राभिषेक करें एवं अनाथालय में अन्न व चांदी का दान करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Saturn-Rahu aspect on 7th/8th axis creating acute severance friction',
          relevantHouses: [6, 7, 8, 12],
          relevantVargas: ['D1', 'D9 Navamsha'],
          ashtakavargaBindus: h8,
          primaryPlanets: ['Mars', 'Saturn', 'Rahu', 'Venus'],
          shlokaCitationEn: 'Phaladeepika Ch. 10: Cruel malefics in 7th without benefic aspect cause bitter severance of the nuptial knot.',
          shlokaCitationHi: 'फलदीपिका: सप्तम में क्रूर पाप ग्रहों की स्थिति दांपत्य विच्छेद का कारण बनती है।'
        }
      };
    }
  },

  // --------------------------------------------------------------------------
  // SPHERE III: DHARMA & VIKAS (Inheritance, Property, Scandals & Justice)
  // --------------------------------------------------------------------------
  {
    id: 'q_inheritance_batwara_timing',
    sphereId: 'dharma',
    domainId: 'inheritance_estate',
    domainNameEn: 'Inheritance, Ancestral Land & Partition (Batwara)',
    domainNameHi: 'पैतृक संपत्ति, वसीयत एवं पारिवारिक बँटवारा',
    questionEn: 'When will I receive my rightful ancestral inheritance, and will siblings/relatives contest the partition?',
    questionHi: 'मुझे पैतृक संपत्ति व वसीयत का हिस्सा कब मिलेगा, और क्या भाई-बहन या संबंधी बँटवारे में विवाद करेंगे?',
    iconName: 'Home',
    keywords: ['inheritance', 'ancestral', 'property', 'land', 'will', 'batwara', 'paitrik', 'partition', 'family estate', 'vasiyat'],
    popularChip: true,
    karmicElasticity: 'DRIDHA_ADRIDHA',
    evaluate: (kundali, lang) => {
      const h8 = getHouseBindus(kundali, 8);
      const h4 = getHouseBindus(kundali, 4);
      const h9 = getHouseBindus(kundali, 9);
      const marsH = getPlanetHouse(kundali, 'Mars');
      const satH = getPlanetHouse(kundali, 'Saturn');
      const dasha = getActiveDasha(kundali);

      const hasPartitionDispute = marsH === 3 || marsH === 8 || satH === 3;

      return {
        questionId: 'q_inheritance_batwara_timing',
        verdictEn: 'Substantial Ancestral Legacy Promised (8th/9th Bhava Fortification). Legal title transfer will materialize in the indicated window.',
        verdictHi: 'कुंडली में पैतृक संपत्ति व भू-अधिकार का प्रबल योग है। अष्टम व नवम भाव का प्रभाव दर्शाता है कि विधिक स्वामित्व आपके नाम अवश्य आएगा।',
        status: 'PROMISED',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Estate & Legacy Settlement Window'),
        confidenceScore: 91,
        karmicElasticity: 'DRIDHA_ADRIDHA',
        karmicElasticityBadgeEn: 'RESOLVABLE KARMA: SIBLING MEDIATION PREVENTS COURT LITIGATION',
        karmicElasticityBadgeHi: 'साध्य कर्म: आपसी मध्यस्थता से अदालती मुकदमेबाजी टलेगी',
        strategicDirectionEn: hasPartitionDispute
          ? 'Sign a registered Family Settlement Deed (Batwara) with a neutral elder witness; avoid verbal arrangements.'
          : 'Maintain complete harmony with co-borns; the ancestral transfer will be peaceful and unanimous.',
        strategicDirectionHi: hasPartitionDispute
          ? 'वरिष्ठों की उपस्थिति में विधिक रूप से पंजीकृत पारिवारिक विभाजन पत्र (बँटवारा) तैयार कराएं; मौखिक वादों पर न रहें।'
          : 'भाई-बहनों से मधुर संबंध रखें; संपत्ति का हस्तांतरण सर्वसम्मति से शांतिपूर्ण होगा।',
        catalystEn: 'A respected paternal uncle or senior family lawyer who commands trust across all branches.',
        catalystHi: 'एक निष्पक्ष व सम्मानित चाचा/ताऊ अथवा विश्वस्त वरिष्ठ पारिवारिक वकील।',
        trapdoorEn: 'Hidden municipal encumbrances, unpaid agricultural taxes, or encroaching tenants.',
        trapdoorHi: 'संपत्ति पर पुराना कर बकाया अथवा अवैध किराएदार का कब्जा।',
        vyayaCostEn: '12th from 4th (3rd house): Temporary friction with brothers/cousins requiring emotional generosity.',
        vyayaCostHi: 'बंधु-बांधवों से तात्कालिक मनमुटाव; शांति हेतु छोटे हिस्से में उदारता की आवश्यकता।',
        surgicalUpayaEn: 'Perform Tarpan for departed ancestors on Amavasya; light a mustard oil lamp under a Peepal tree on Saturdays.',
        surgicalUpayaHi: 'अमावस्या को पितरों का तर्पण करें एवं शनिवार को पीपल के नीचे सरसों के तेल का दीपक जलाएं।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Jupiter transiting 4th/9th trine while Saturn watches 8th house',
          relevantHouses: [8, 9, 4, 3, 2],
          relevantVargas: ['D1', 'D4 Chaturthamsa', 'D8'],
          ashtakavargaBindus: h8 + h4,
          primaryPlanets: ['Saturn', 'Jupiter', 'Mars', 'Sun'],
          shlokaCitationEn: 'BPHS Ch. 18: When 8th lord is in Kendra aspected by 9th lord, the native inherits vast ancestral treasures without litigation.',
          shlokaCitationHi: 'बृहत्पाराशर होराशास्त्र: अष्टमेश यदि केंद्र में नवमेश से दृष्ट हो, तो जातक को बिना विवाद विपुल पैतक संपदा प्राप्त होती है।'
        },
        twoPhaseTiming: {
          phase1: {
            titleEn: 'PHASE 1: INITIATION & NOTICE GATE',
            titleHi: 'प्रथम चरण: वसीयत/विधिक नोटिस व प्रक्रिया आरंभ',
            datesEn: resolveDynamicDashaWindow(kundali, 'Notice & Filings'),
            datesHi: resolveDynamicDashaWindow(kundali, 'विधिक नोटिस व दाखिला'),
            triggerEn: '8th Bhava Solar Ingress & Pratyantar Confluence',
            triggerHi: 'अष्टम भाव सूर्य गोचर व प्रत्यंतर संमिलन',
            actionEn: 'Probate filing, title deeds verification & partition paperwork',
            actionHi: 'वसीयत प्रोबेट दाखिल, विधिक नोटिस व कागजातों की प्राथमिक जांच'
          },
          phase2: {
            titleEn: 'PHASE 2: FINAL SETTLEMENT & REGISTRATION',
            titleHi: 'द्वितीय चरण: विधिक बँटवारा रजिस्ट्री व अधिकार हस्तांतरण',
            datesEn: resolveDynamicDashaWindow(kundali, 'Final Settlement'),
            datesHi: resolveDynamicDashaWindow(kundali, 'अंतिम समझौता व रजिस्ट्री'),
            triggerEn: 'Jupiter Aspect on 8th House of Legacies',
            triggerHi: 'गुरु का अष्टम भाव पर शुभ दृष्टि गोचर',
            actionEn: 'Registered Batwara deed execution, revenue mutation & possession',
            actionHi: 'विधिक बँटवारा विलेख पंजीकरण, नामांतरण व स्वामित्व प्राप्ति'
          }
        }
      };
    }
  },
  {
    id: 'q_scandals_loss_of_face',
    sphereId: 'dharma',
    domainId: 'reputation_crisis',
    domainNameEn: 'Scandals, Defamation & Loss of Face',
    domainNameHi: 'कलंक, मानहानि, षड्यंत्र एवं सामाजिक प्रतिष्ठा',
    questionEn: 'Will hidden enemies, false allegations, or media scandals damage my reputation, and when will honor be restored?',
    questionHi: 'क्या गुप्त शत्रु, झूठे आरोप या मीडिया कलंक मेरी प्रतिष्ठा को नुकसान पहुंचाएंगे, और सम्मान कब बहाल होगा?',
    iconName: 'Shield',
    keywords: ['scandal', 'defamation', 'reputation', 'loss of face', 'allegations', 'fir', 'kalank', 'apman', 'enemies'],
    popularChip: false,
    karmicElasticity: 'DRIDHA_ADRIDHA',
    evaluate: (kundali, lang) => {
      const rahuH = getPlanetHouse(kundali, 'Rahu');
      const h6 = getHouseBindus(kundali, 6);
      const h10 = getHouseBindus(kundali, 10);
      const dasha = getActiveDasha(kundali);

      const hasScandalVulnerability = rahuH === 10 || rahuH === 8 || rahuH === 1;

      return {
        questionId: 'q_scandals_loss_of_face',
        verdictEn: hasScandalVulnerability
          ? 'Temporary Smear & Shadow Phase Detected (Rahu-afflicted 10th/8th axis). Enemies will attempt public defamation, but Shatru-Hanta Yoga ensures complete exoneration.'
          : 'High Armor of Integrity (Amala Yoga). False rumors will dissipate without sticking to your public standing.',
        verdictHi: hasScandalVulnerability
          ? 'तात्कालिक मानहानि व षड्यंत्र का योग (राहु का अष्टम/दशम पर प्रभाव)। विरोधी झूठे आरोप लगाने का प्रयास करेंगे, परंतु शत्रुहंता योग अंततः आपको निष्कलंक सिद्ध करेगा।'
          : 'अमल कीर्ति योग विद्यमान है। दुष्प्रचार स्वतः निष्प्रभावी हो जाएगा और आपकी सामाजिक प्रतिष्ठा अक्षुण्ण रहेगी।',
        status: hasScandalVulnerability ? 'CHALLENGING' : 'FAVORABLE',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Legal Vigilance & Defense Window'),
        confidenceScore: 86,
        karmicElasticity: 'DRIDHA_ADRIDHA',
        karmicElasticityBadgeEn: 'DEFENSIVE POSTURE: DO NOT PROVOKE IN MEDIA/PRESS',
        karmicElasticityBadgeHi: 'रक्षात्मक नीति: मीडिया में अनावश्यक बयानबाजी से बचें',
        strategicDirectionEn: 'Do not enter into emotional public debates; allow legal counsel to respond through documented facts.',
        strategicDirectionHi: 'सार्वजनिक विवाद में स्वयं न कूदें; केवल विधिक प्रक्रिया व प्रमाणों द्वारा ही उत्तर दें।',
        catalystEn: 'A bold criminal defence attorney or high-integrity investigative authority.',
        catalystHi: 'एक कुशल वरिष्ठ अधिवक्ता अथवा निष्पक्ष जांच अधिकारी।',
        trapdoorEn: 'Confiding confidential strategies to unverified associates in your inner circle.',
        trapdoorHi: 'अपने ही किसी नजदीकी सहयोगी पर आंख मूंदकर विश्वास करना जो गुप्त बातें लीक कर दे।',
        vyayaCostEn: 'High expenditure on legal retainers and public relations management.',
        vyayaCostHi: 'विधिक शुल्क व जनसंपर्क सुधार में धन का व्यय।',
        surgicalUpayaEn: 'Recite Batuk Bhairava Kavach or Durga Saptashati Argala Stotra on Tuesdays.',
        surgicalUpayaHi: 'मंगलवार को बटुक भैरव कवच अथवा दुर्गा सप्तशती के अर्गला स्तोत्र का पाठ करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Rahu nodal transit over sensitive Bhava creating smoke and mirrors',
          relevantHouses: [6, 8, 10, 12],
          relevantVargas: ['D1', 'D10', 'D6'],
          ashtakavargaBindus: h6,
          primaryPlanets: ['Rahu', 'Mars', 'Sun', 'Saturn'],
          shlokaCitationEn: 'Saravali: When Mars and Rahu cast aspect on the 10th lord, envious rivals concoct public slander, which dissolves under Jupiter’s glance.',
          shlokaCitationHi: 'सारावली: दशमेश पर राहु-मंगल की दृष्टि से ईर्ष्यालु शत्रु अपयश फैलाते हैं, किंतु गुरु दृष्टि से सत्य की विजय होती है।'
        }
      };
    }
  },

  // --------------------------------------------------------------------------
  // SPHERE IV: MOKSHA & PRAVAS (Foreign Lands, Health/Surgery & Awakening)
  // --------------------------------------------------------------------------
  {
    id: 'q_foreign_pr_vs_homeland',
    sphereId: 'moksha',
    domainId: 'foreign_horizons',
    domainNameEn: 'Foreign PR / Citizenship vs. Return to Homeland',
    domainNameHi: 'विदेशी नागरिकता (PR) बनाम स्वदेश वापसी',
    questionEn: 'Will I settle permanently abroad with PR/citizenship, or will destiny pull me back to settle in my homeland?',
    questionHi: 'क्या मैं विदेश में स्थायी नागरिकता (पीआर) लेकर बसूंगा, अथवा भाग्य मुझे वापस स्वदेश खींचेगा?',
    iconName: 'Globe',
    keywords: ['foreign', 'settlement', 'pr', 'citizenship', 'abroad', 'visa', 'homeland', 'return', 'greencard', 'nri'],
    popularChip: true,
    karmicElasticity: 'DRIDHA_ADRIDHA',
    evaluate: (kundali, lang) => {
      const h4 = getHouseBindus(kundali, 4);
      const h12 = getHouseBindus(kundali, 12);
      const rahuH = getPlanetHouse(kundali, 'Rahu');
      const moonH = getPlanetHouse(kundali, 'Moon');
      const dasha = getActiveDasha(kundali);

      const isPermanentForeign = (rahuH === 12 || rahuH === 9 || rahuH === 4) && h12 >= h4;

      return {
        questionId: 'q_foreign_pr_vs_homeland',
        verdictEn: isPermanentForeign
          ? 'Permanent Overseas Settlement & Foreign Citizenship Promised. Deep 12th/9th house Rahu connection favors life far away from birthplace.'
          : 'High Success Abroad, but Eventual Return to Homeland (Ghar Vapasi) is Destined. A fortified 4th house and Motherland connection brings ultimate peace back home.',
        verdictHi: isPermanentForeign
          ? 'स्थायी विदेशी नागरिकता (पीआर) का पूर्ण योग। नवम व द्वादश भाव में राहु का संबंध जन्मभूमि से दूर दीर्घकालिक समृद्धि दर्शाता है।'
          : 'विदेश में भारी सफलता मिलेगी, परंतु अंततः स्वदेश वापसी (घर वापसी) तय है। चतुर्थ भाव की मजबूती आपको अपनी जड़ों में वापस लाएगी।',
        status: 'PROMISED',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Overseas Visa & Ingress Activation'),
        confidenceScore: 90,
        karmicElasticity: 'DRIDHA_ADRIDHA',
        karmicElasticityBadgeEn: 'FREE WILL INVOLVED: TAX PLANNING & IMMIGRATION FILINGS COUNT',
        karmicElasticityBadgeHi: 'कर्म-संवेदनशील: विधिक कागजात व कर-नियोजन महत्वपूर्ण',
        strategicDirectionEn: isPermanentForeign
          ? 'File PR/citizenship documents during Jupiter transit; invest surplus earnings in overseas liquid assets.'
          : 'Accumulate foreign currency capital for 5–7 years; reinvest into enterprise or estate in your home nation.',
        strategicDirectionHi: isPermanentForeign
          ? 'गुरु के शुभ गोचर में पीआर आवेदन करें; अतिरिक्त धन को विदेशी संपत्तियों में लगाएं।'
          : 'विदेश में ५-७ वर्ष पूंजी संचय करें और उसे अपने देश में उद्योग अथवा संपत्ति में लगाकर वापस लौटें।',
        catalystEn: 'An international employer sponsor or licensed immigration attorney.',
        catalystHi: 'विदेशी नियोक्ता अथवा अधिकृत आव्रजन सलाहकार।',
        trapdoorEn: 'Administrative visa processing delays during Mercury retrograde windows.',
        trapdoorHi: 'बुध के वक्री काल में वीजा कागजातों में तकनीकी त्रुटि या विलंब।',
        vyayaCostEn: 'Emotional distance from aging parents and native cultural roots.',
        vyayaCostHi: 'वृद्ध माता-पिता व पैतृक संस्कृति से शारीरिक दूरी का भावनात्मक मूल्य।',
        surgicalUpayaEn: 'Feed migratory aquatic life (fish) with wheat balls on Wednesdays; donate mustard oil.',
        surgicalUpayaHi: 'बुधवार को मछलियों को आटे की गोलियां खिलाएं एवं छाया दान करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Jupiter aspecting 12th house of distant lands and foreign residence',
          relevantHouses: [12, 9, 4, 3, 7],
          relevantVargas: ['D1', 'D9', 'D12'],
          ashtakavargaBindus: h12,
          primaryPlanets: ['Rahu', 'Moon', 'Jupiter', 'Saturn'],
          shlokaCitationEn: 'BPHS Ch. 24: When the 12th lord occupies a movable sign aspected by Rahu, the native establishes an enduring home beyond seas.',
          shlokaCitationHi: 'बृहत्पाराशर होराशास्त्र: द्वादशेश चर राशि में राहु से दृष्ट हो तो जातक सात समुद्र पार स्थायी आशियाना बनाता है।'
        }
      };
    }
  },
  {
    id: 'q_health_surgery_longevity',
    sphereId: 'moksha',
    domainId: 'health_vitality',
    domainNameEn: 'Surgeries, Medical Trauma & Recovery Window',
    domainNameHi: 'शल्य-चिकित्सा (सर्जरी), गंभीर स्वास्थ्य एवं आरोग्य काल',
    questionEn: 'Will I need surgical intervention or hospitalization, and what is the exact recovery window?',
    questionHi: 'क्या मुझे सर्जरी अथवा अस्पताल में भर्ती होना पड़ेगा, और पूर्ण स्वास्थ्य लाभ का सही समय क्या है?',
    iconName: 'Activity',
    keywords: ['surgery', 'health', 'hospital', 'disease', 'recovery', 'operation', 'shalya', 'ayurveda', 'swasthya', 'illness'],
    popularChip: false,
    karmicElasticity: 'DRIDHA_ADRIDHA',
    evaluate: (kundali, lang) => {
      const marsH = getPlanetHouse(kundali, 'Mars');
      const satH = getPlanetHouse(kundali, 'Saturn');
      const h6 = getHouseBindus(kundali, 6);
      const h8 = getHouseBindus(kundali, 8);
      const dasha = getActiveDasha(kundali);

      const hasSurgeryTrigger = (marsH === 6 || marsH === 8 || marsH === 12);

      return {
        questionId: 'q_health_surgery_longevity',
        verdictEn: hasSurgeryTrigger
          ? 'Surgical Intervention Indicated (Mars in Trika Bhava). However, with Jupiter aspecting Lagna, procedure will be curative with complete post-operative recovery.'
          : 'Non-Invasive Protocol Highly Favored. Condition can be managed and reversed through diet, targeted Ayurveda/Yoga, and lifestyle moderation.',
        verdictHi: hasSurgeryTrigger
          ? 'शल्य-चिकित्सा (सर्जरी) का योग (मंगल का त्रिक भाव में प्रभाव)। परंतु लग्न पर गुरु की दृष्टि के कारण सर्जरी पूर्णतः सफल व रोगमुक्त करने वाली होगी।'
          : 'गैर-शल्य (बिना सर्जरी) उपचार ही श्रेष्ठ रहेगा। उचित आहार, आयुर्वेद व जीवनशैली में सुधार से यह व्याधि पूर्णतः ठीक हो सकती है।',
        status: hasSurgeryTrigger ? 'DECISION_CRITICAL' : 'FAVORABLE',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Restorative Wellness & Medical Window'),
        confidenceScore: 89,
        karmicElasticity: 'DRIDHA_ADRIDHA',
        karmicElasticityBadgeEn: 'MEDICAL DISCIPLINE: SECOND OPINION & TIMED MUHURTA CRITICAL',
        karmicElasticityBadgeHi: 'चिकित्सीय सतर्कता: द्वितीय परामर्श व शुभ मुहूर्त आवश्यक',
        strategicDirectionEn: 'Select an experienced chief surgeon; avoid emergency procedures during lunar eclipse or Rahu-Kaal.',
        strategicDirectionHi: 'अनुभवी वरिष्ठ शल्य-चिकित्सक का ही चयन करें; ग्रहण काल या राहुकाल में ऐच्छिक सर्जरी न कराएं।',
        catalystEn: 'A high-dexterity surgeon or compassionate integrative medical specialist.',
        catalystHi: 'एक कुशल वरिष्ठ सर्जन अथवा निष्ठावान आयुर्वेद विशेषज्ञ।',
        trapdoorEn: 'Prematurely discontinuing post-operative medications or ignoring rest.',
        trapdoorHi: 'सर्जरी के तुरंत बाद विश्राम की अनदेखी करना या दवाएं बीच में छोड़ना।',
        vyayaCostEn: 'Requires temporary cessation of intense commercial engagements.',
        vyayaCostHi: 'व्यावसायिक कार्यों से अनिवार्य विश्राम।',
        surgicalUpayaEn: 'Recite Maha Mrityunjaya Mantra 108 times daily; donate blood or medicines to poor patients on Tuesdays.',
        surgicalUpayaHi: 'प्रतिदिन महामृत्युंजय मंत्र की १ माला जपें एवं मंगलवार को निर्धन रोगियों को दवा का दान करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Mars transit triggering 6th/8th house scalpel influence neutralized by Jupiter',
          relevantHouses: [6, 8, 12, 1],
          relevantVargas: ['D1', 'D6', 'D28 Trisamsha'],
          ashtakavargaBindus: h6,
          primaryPlanets: ['Mars', 'Saturn', 'Jupiter', 'Sun'],
          shlokaCitationEn: 'Phaladeepika Ch. 14: When Mars rules the 6th or 8th, wounds by blade manifest; when Jupiter protects Lagna, the body is quickly restored.',
          shlokaCitationHi: 'फलदीपिका: षष्ठ/अष्टम में मंगल शस्त्र-शल्य का कारक बनता है, किंतु गुरु के लग्न को देखने से देह शीघ्र स्वस्थ हो जाती है।'
        }
      };
    }
  },
  {
    id: 'q_debt_relief_bankruptcy',
    sphereId: 'artha',
    domainId: 'debt_relief',
    domainNameEn: 'Debt Relief, Loan Clearance & Bankruptcy Turnaround (Rina Mukti)',
    domainNameHi: 'ऋण मुक्ति, कर्ज से छुटकारा एवं दिवालियापन निवारण',
    questionEn: 'When will I become completely debt-free, and will creditors or banks initiate legal attachment of assets?',
    questionHi: 'मुझे कर्ज व ऋण से पूर्ण मुक्ति कब मिलेगी, और क्या बैंक या लेनदार संपत्ति कुर्क करेंगे?',
    iconName: 'Shield',
    keywords: ['debt', 'loan', 'bankruptcy', 'karza', 'rina', 'bank', 'creditors', 'emi', 'mortgage', 'insolvency'],
    popularChip: true,
    karmicElasticity: 'DRIDHA_ADRIDHA',
    evaluate: (kundali, lang) => {
      const satH = getPlanetHouse(kundali, 'Saturn');
      const marsH = getPlanetHouse(kundali, 'Mars');
      const h6 = getHouseBindus(kundali, 6);
      const h11 = getHouseBindus(kundali, 11);
      const dasha = getActiveDasha(kundali);

      const hasDebtResolution = h11 >= h6 || satH === 6 || marsH === 6;

      return {
        questionId: 'q_debt_relief_bankruptcy',
        verdictEn: hasDebtResolution
          ? 'Rina Mukti (Debt Liberation) Yoga Confirmed. The 11th house of inflow overpowers 6th-house liabilities; structured repayment and full asset protection materialize.'
          : 'High Debt Vulnerability (6th/8th axis burdened). Immediate debt restructuring, moratorium filing, or asset liquidation required to prevent judicial attachment.',
        verdictHi: hasDebtResolution
          ? 'ऋण मुक्ति योग प्रमाणित। एकादश भाव (आय) षष्ठ भाव (ऋण) से अधिक बली है; सुनियोजित किश्तों द्वारा संपूर्ण कर्ज समाप्त होगा और संपत्ति सुरक्षित रहेगी।'
          : 'ऋण का भारी दबाव (षष्ठ-अष्टम का प्रभाव)। कुर्की अथवा विधिक कार्रवाई से बचने हेतु तत्काल कर्ज पुनर्गठन (रीस्ट्रक्चरिंग) अथवा एकमुश्त समझौता आवश्यक है।',
        status: hasDebtResolution ? 'FAVORABLE' : 'CHALLENGING',
        timingWindow: resolveDynamicDashaWindow(kundali, '11th Lord Debt Liberation Window'),
        confidenceScore: 91,
        karmicElasticity: 'DRIDHA_ADRIDHA',
        karmicElasticityBadgeEn: 'FINANCIAL PRUDENCE: NO NEW CREDIT CARDS OR UNSECURED BORROWING',
        karmicElasticityBadgeHi: 'वित्तीय संयम: कोई नया व्यक्तिगत कर्ज अथवा क्रेडिट कार्ड न लें',
        strategicDirectionEn: 'Consolidate high-interest unorganized loans into a single low-interest collateralized debt; clear highest APR first.',
        strategicDirectionHi: 'महंगे ब्याज वाले कर्जों को एक सुरक्षित बैंक लोन में समेटें; सबसे महंगे ऋण को पहले समाप्त करें।',
        catalystEn: 'A financial restructuring consultant, debt arbitrator, or institutional banker.',
        catalystHi: 'एक वित्तीय सलाहकार, ऋण मध्यस्थ अथवा बैंक प्रबंधक।',
        trapdoorEn: 'Borrowing from loan sharks or fresh personal loans to pay existing bank EMIs.',
        trapdoorHi: 'पुरानी किश्तें भरने के लिए नया असुरक्षित कर्ज लेना (कर्ज का कुचक्र)।',
        vyayaCostEn: 'Requires selling non-core lifestyle assets to preserve ancestral property.',
        vyayaCostHi: 'पैतृक घर बचाने हेतु गैर-ज़रूरी विलासिता संपत्ति का त्याग।',
        surgicalUpayaEn: 'Recite Rina Vimochana Mangala Stotra every Tuesday; offer red lentils and jaggery to Hanumanji.',
        surgicalUpayaHi: 'प्रति मंगलवार ऋणमोचन मंगल स्तोत्र का पाठ करें एवं हनुमानजी को गुड़-चना व मसूर की दाल अर्पित करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Jupiter aspecting 11th house of wealth inflow to dissolve 6th house liabilities',
          relevantHouses: [6, 11, 2, 8],
          relevantVargas: ['D1', 'D2 Hora', 'D6'],
          ashtakavargaBindus: h11,
          primaryPlanets: ['Mars', 'Saturn', 'Jupiter', 'Mercury'],
          shlokaCitationEn: 'BPHS Ch. 22: When 11th lord is stronger than 6th lord, the native conquers all debts and accumulates lasting treasure.',
          shlokaCitationHi: 'बृहत्पाराशर होराशास्त्र: षष्ठेश से एकादशेश बली होने पर जातक समस्त ऋणों को समाप्त कर धनवान बनता है।'
        }
      };
    }
  },
  {
    id: 'q_education_competitive_exam',
    sphereId: 'dharma',
    domainId: 'education_exams',
    domainNameEn: 'Higher Education, Competitive Exams & Academic Research',
    domainNameHi: 'उच्च शिक्षा, प्रतियोगी परीक्षा (UPSC/IIT/NEET) एवं शोध',
    questionEn: 'When will I clear prestigious competitive entrance exams, and which field of study guarantees the highest scholarly achievement?',
    questionHi: 'मैं प्रतिष्ठित प्रतियोगी परीक्षा कब उत्तीर्ण करूंगा, और कौन सा अध्ययन क्षेत्र मुझे सर्वोच्च ख्याति दिलाएगा?',
    iconName: 'BookOpen',
    keywords: ['education', 'upsc', 'exam', 'iit', 'neet', 'college', 'phd', 'vidya', 'study', 'research', 'degree'],
    popularChip: true,
    karmicElasticity: 'ADRIDHA',
    evaluate: (kundali, lang) => {
      const mercH = getPlanetHouse(kundali, 'Mercury');
      const jupH = getPlanetHouse(kundali, 'Jupiter');
      const sunH = getPlanetHouse(kundali, 'Sun');
      const h5 = getHouseBindus(kundali, 5);
      const h6 = getHouseBindus(kundali, 6);
      const dasha = getActiveDasha(kundali);

      const hasExamSuccess = h6 >= 28 && (mercH === 5 || mercH === 1 || mercH === 9 || jupH === 5 || sunH === 10);

      return {
        questionId: 'q_education_competitive_exam',
        verdictEn: hasExamSuccess
          ? 'Exceptional Intellect & Competitive Triumph (Saraswati-Shatru Hanta Yoga). Deep analytical grasp ensures high percentile rank in national competitive exams.'
          : 'High Academic Merit in Research & Concept Mastery, but competitive MCQ speed tests require structured mock drilling and test anxiety management.',
        verdictHi: hasExamSuccess
          ? 'असाधारण मेधा एवं प्रतियोगी विजय (सरस्वती-शत्रुहंता योग)। गहन बौद्धिक क्षमता आपको राष्ट्रीय स्तर की परीक्षा में शीर्ष रैंक दिलाएगी।'
          : 'गहन अध्ययन व शोध में उत्तम सफलता, किंतु समयबद्ध प्रतियोगी परीक्षाओं हेतु निरंतर मॉक टेस्ट व परीक्षा-तनाव पर नियंत्रण आवश्यक है।',
        status: hasExamSuccess ? 'PROMISED' : 'FAVORABLE',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Academic & Competitive Examination Gate'),
        confidenceScore: 92,
        karmicElasticity: 'ADRIDHA',
        karmicElasticityBadgeEn: 'HIGHLY ELASTIC: DEDICATED STUDY SCHEDULE DIRECTLY SHAPES THE OUTCOME',
        karmicElasticityBadgeHi: 'कर्म-साध्य: अनुशासित समय-सारिणी से परिणाम को पूरी तरह बदला जा सकता है',
        strategicDirectionEn: 'Focus intensely on conceptual problem solving; study during early Brahma Muhurta when Mercury/Jupiter align.',
        strategicDirectionHi: 'ब्राह्ममुहूर्त में अध्ययन करें; रटने की अपेक्षा मूलभूत सिद्धांतों पर अधिक ध्यान दें।',
        catalystEn: 'A visionary professor, academy mentor, or academic study peer.',
        catalystHi: 'एक अनुभवी प्रोफेसर, गुरु अथवा अनुशासित अध्ययन-सहपाठी।',
        trapdoorEn: 'Digital screen distraction and last-minute exam hall panic during Rahu sub-transit.',
        trapdoorHi: 'राहु के प्रभाव में डिजिटल भटकाव अथवा परीक्षा हॉल में घबराहट।',
        vyayaCostEn: 'Requires temporary social isolation and sacrifice of entertainment.',
        vyayaCostHi: 'सामाजिक जीवन व मनोरंजन का पूर्ण त्याग।',
        surgicalUpayaEn: 'Chant Saraswati Stotram daily before sunrise; consume a basil (Tulsi) leaf with holy water.',
        surgicalUpayaHi: 'प्रतिदिन सूर्योदय पूर्व सरस्वती स्तोत्र का पाठ करें एवं तुलसी दल का सेवन करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Jupiter transiting 5th/9th house conferring Vidyakaraka Saraswati grace',
          relevantHouses: [4, 5, 6, 9, 10],
          relevantVargas: ['D1', 'D24 Chaturvimshamsha', 'D9'],
          ashtakavargaBindus: h5 + h6,
          primaryPlanets: ['Mercury', 'Jupiter', 'Sun', 'Mars'],
          shlokaCitationEn: 'BPHS Ch. 15: When 5th lord is with Jupiter and Mercury in Kendra/Trikona, the native becomes a master of sciences and shastras.',
          shlokaCitationHi: 'बृहत्पाराशर होराशास्त्र: पंचमेश गुरु-बुध के साथ केंद्र-त्रिकोण में हो तो जातक समस्त विद्याओं व शास्त्रों में पारंगत होता है।'
        }
      };
    }
  },
  {
    id: 'q_commercial_business_launch',
    sphereId: 'artha',
    domainId: 'business_enterprise',
    domainNameEn: 'Startup Incorporation, Venture Launch & Commercial Expansion',
    domainNameHi: 'नया व्यवसाय, स्टार्टअप स्थापना एवं वाणिज्यिक विस्तार',
    questionEn: 'What is the auspicious muhurta window to incorporate my business, raise funding, or launch a new commercial venture?',
    questionHi: 'मेरा नया व्यवसाय शुरू करने, फंडिंग जुटाने अथवा स्टार्टअप लांच करने का सबसे शुभ कालखंड कौन सा है?',
    iconName: 'Award',
    keywords: ['business', 'startup', 'launch', 'incorporation', 'venture', 'expansion', 'investor', 'equity', 'trade'],
    popularChip: true,
    karmicElasticity: 'DRIDHA_ADRIDHA',
    evaluate: (kundali, lang) => {
      const mercH = getPlanetHouse(kundali, 'Mercury');
      const h7 = getHouseBindus(kundali, 7);
      const h10 = getHouseBindus(kundali, 10);
      const h11 = getHouseBindus(kundali, 11);
      const dasha = getActiveDasha(kundali);

      const hasCommercialStrength = h7 >= 28 && h10 >= 30;

      return {
        questionId: 'q_commercial_business_launch',
        verdictEn: hasCommercialStrength
          ? 'Golden Commercial Launch Gate (7th/10th Bhava Vyapar Yoga). Highly auspicious for enterprise registration, vendor contracts, and client acquisition.'
          : 'Favorable for Soft Launch & Pilot Testing. Delay heavy capital commitments until the verified auspicious Dasha window begins.',
        verdictHi: hasCommercialStrength
          ? 'स्वर्ण व्यापार द्वार (सप्तम-दशम व्यापार योग)। कंपनी पंजीकरण, अनुबंधों पर हस्ताक्षर एवं व्यावसायिक विस्तार हेतु अत्यंत कल्याणकारी समय।'
          : 'पायलट प्रोजेक्ट अथवा छोटे स्तर पर शुरुआत हेतु उत्तम। बड़ी पूंजी का निवेश आगामी शुभ दशा प्रारंभ होने तक स्थगित रखें।',
        status: hasCommercialStrength ? 'PROMISED' : 'FAVORABLE',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Enterprise Inception & Ingress Window'),
        confidenceScore: 93,
        karmicElasticity: 'DRIDHA_ADRIDHA',
        karmicElasticityBadgeEn: 'EXECUTION DEPENDENT: LEGAL CHARTER & CASH RUNWAY MANAGEMENT VITAL',
        karmicElasticityBadgeHi: 'क्रियान्वयन-आधारित: विधिक अनुबंध व नकदी प्रवाह प्रबंधन अनिवार्य',
        strategicDirectionEn: 'Incorporate during Shukla Paksha on a Wednesday/Friday; secure 12-month operating runway before scaling burn.',
        strategicDirectionHi: 'शुक्ल पक्ष के बुधवार/शुक्रवार को विधिवत आरंभ करें; कम से कम १२ माह का बैकअप फंड रखें।',
        catalystEn: 'An early enterprise anchor customer, strategic angel investor, or co-founder.',
        catalystHi: 'एक मुख्य कॉर्पोरेट ग्राहक, देवदूत निवेशक (Angel) अथवा योग्य सह-संस्थापक।',
        trapdoorEn: 'Over-committing to luxury office space and premature marketing burn before product-market fit.',
        trapdoorHi: 'उत्पाद की वास्तविक मांग से पहले ही विलासिता व दिखावे में भारी धन व्यय।',
        vyayaCostEn: 'Requires reinvesting all personal profits into business reserves for first 3 years.',
        vyayaCostHi: 'प्रथम ३ वर्षों तक निजी सुख-सुविधाओं का त्याग कर लाभ को व्यापार में पुनः लगाना।',
        surgicalUpayaEn: 'Keep a consecrated Mercury Kuber Yantra in cash-box/office; donate green moong dal on Wednesdays.',
        surgicalUpayaHi: 'कार्यालय की तिजोरी में सिद्ध कुबेर यंत्र स्थापित करें एवं बुधवार को साबुत मूंग दान करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Transit Mercury and Jupiter conferring Shubha Kartari Yoga on 10th and 7th houses',
          relevantHouses: [7, 10, 11, 3],
          relevantVargas: ['D1', 'D10 Dashamsha', 'D9'],
          ashtakavargaBindus: h7 + h10,
          primaryPlanets: ['Mercury', 'Jupiter', 'Venus', 'Mars'],
          shlokaCitationEn: 'Saravali Ch. 35: When Mercury occupies a Kendra and Venus joins 11th, the merchant achieves sovereign prosperity through trade.',
          shlokaCitationHi: 'सारावली: केंद्र में बुध व एकादश में शुक्र होने से व्यापारी वाणिज्य द्वारा अतुलनीय समृद्धि प्राप्त करता है।'
        }
      };
    }
  },
  {
    id: 'q_vehicle_purchase_comfort',
    sphereId: 'artha',
    domainId: 'vehicle_comfort',
    domainNameEn: 'Vehicles, Luxury Conveyances & Travel Safety (Vahana Yoga)',
    domainNameHi: 'वाहन सुख, नई गाड़ी की खरीद एवं यात्रा सुरक्षा (वाहन योग)',
    questionEn: 'When will I purchase my dream vehicle or four-wheeler, and does my chart indicate road collision risks?',
    questionHi: 'मैं अपना नया चार पहिया वाहन कब खरीदूंगा, और क्या कुंडली में यात्रा/दुर्घटना का कोई योग है?',
    iconName: 'Home',
    keywords: ['vehicle', 'car', 'automobile', 'accident', 'safety', 'gadi', 'vahana', 'luxury car', 'travel'],
    popularChip: false,
    karmicElasticity: 'ADRIDHA',
    evaluate: (kundali, lang) => {
      const venH = getPlanetHouse(kundali, 'Venus');
      const marsH = getPlanetHouse(kundali, 'Mars');
      const h4 = getHouseBindus(kundali, 4);
      const dasha = getActiveDasha(kundali);

      const hasVahanaYoga = (venH === 4 || venH === 1 || venH === 9) || h4 >= 30;

      return {
        questionId: 'q_vehicle_purchase_comfort',
        verdictEn: hasVahanaYoga
          ? 'Strong Vahana Sukha Yoga (4th House Fortification). Auspicious window for acquiring a premium, safe, and comfortable conveyance.'
          : 'Utility Conveyance Recommended. Ensure vehicle insurance is robust; avoid rash high-speed driving during Mars-Rahu transits.',
        verdictHi: hasVahanaYoga
          ? 'प्रबल वाहन सुख योग (चतुर्थ भाव बली)। सुखद, सुरक्षित एवं मनपसंद चार पहिया वाहन क्रय करने का अत्यंत शुभ काल।'
          : 'साधारण व व्यावहारिक वाहन ही लें। बीमा पूर्ण रखें एवं मंगल-राहु के गोचर काल में तीव्र गति से वाहन चलाने से बचें।',
        status: hasVahanaYoga ? 'FAVORABLE' : 'NEEDS_REMEDY',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Conveyance & Asset Ingress Window'),
        confidenceScore: 89,
        karmicElasticity: 'ADRIDHA',
        karmicElasticityBadgeEn: 'MUHURTA SENSITIVE: TAKE DELIVERY DURING ABHIJIT MUHURTA ONLY',
        karmicElasticityBadgeHi: 'मुहूर्त-साध्य: वाहन की डिलीवरी केवल अभिजित अथवा शुभ चौघड़िया में लें',
        strategicDirectionEn: 'Select silver, white, or deep blue colors; conduct Vahan Puja at a Hanuman or Ganesha temple immediately after delivery.',
        strategicDirectionHi: 'सफेद, स्लेटी या गहरे नीले रंग की गाड़ी चुनें; डिलीवरी लेते ही हनुमान मंदिर में वाहन पूजन कराएं।',
        catalystEn: 'An auspicious festival muhurta (Dussehra, Dhanteras, Akshaya Tritiya).',
        catalystHi: 'दशहरा, धनतेरस अथवा अक्षय तृतीया जैसा शुभ पर्व मुहूर्त।',
        trapdoorEn: 'Taking delivery during Rahu-Kaal or buying an unverified second-hand lemon vehicle.',
        trapdoorHi: 'राहुकाल में डिलीवरी लेना अथवा बिना जांचे पुराना दोषपूर्ण वाहन खरीदना।',
        vyayaCostEn: 'Depreciating asset consideration; ensure EMIs do not exceed 15% of monthly cash flow.',
        vyayaCostHi: 'किश्तें मासिक आय के १५% से अधिक न हों।',
        surgicalUpayaEn: 'Keep a consecrated silver Ganesha idol in the dashboard; chant Om Namo Bhagavate Vasudevaya on long drives.',
        surgicalUpayaHi: 'डैशबोर्ड पर चांदी के छोटे गणेशजी स्थापित करें एवं यात्रा के समय ॐ नमो भगवते वासुदेवाय का स्मरण करें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Venus aspecting 4th house of conveyances in harmony with D16 Shodashamsha',
          relevantHouses: [4, 11, 12],
          relevantVargas: ['D1', 'D16 Shodashamsha', 'D4'],
          ashtakavargaBindus: h4,
          primaryPlanets: ['Venus', 'Moon', 'Mars'],
          shlokaCitationEn: 'BPHS Ch. 14: When Venus occupies the 4th house in its own or exaltation sign, the native rides majestic conveyances.',
          shlokaCitationHi: 'बृहत्पाराशर होराशास्त्र: चतुर्थ में स्व/उच्च राशि का शुक्र जातक को राजतुल्य वाहन सुख प्रदान करता है।'
        }
      };
    }
  },
  {
    id: 'q_sibling_support_harmony',
    sphereId: 'dharma',
    domainId: 'siblings_family',
    domainNameEn: 'Siblings Harmony, Social Valor & Family Unity',
    domainNameHi: 'भाई-बहन संबंध, सामाजिक प्रभाव एवं परिवार में सौहार्द',
    questionEn: 'How will relations with my brothers, sisters, and extended family evolve, and will siblings support me in times of crisis?',
    questionHi: 'मेरे भाई-बहनों व निकट संबंधियों के साथ संबंध कैसे रहेंगे, और क्या वे संकट में मेरा साथ देंगे?',
    iconName: 'Users',
    keywords: ['siblings', 'brother', 'sister', 'bhai', 'behan', 'family', 'relations', 'bhatri', 'support', 'sahodara'],
    popularChip: false,
    karmicElasticity: 'ADRIDHA',
    evaluate: (kundali, lang) => {
      const marsH = getPlanetHouse(kundali, 'Mars');
      const jupH = getPlanetHouse(kundali, 'Jupiter');
      const h3 = getHouseBindus(kundali, 3);
      const dasha = getActiveDasha(kundali);

      const hasSiblingHarmony = (jupH === 3 || jupH === 9 || jupH === 11) && marsH !== 8;

      return {
        questionId: 'q_sibling_support_harmony',
        verdictEn: hasSiblingHarmony
          ? 'Deep Sahodara Preeti (Sibling Devotion & Mutual Valor). Brothers and sisters act as pillar supporters during major professional milestones.'
          : 'Occasional Misunderstandings over Property or Inherited Duties. Maintain clear, transparent communication and avoid financial loans between siblings.',
        verdictHi: hasSiblingHarmony
          ? 'प्रबल सहोदर प्रीति (भाई-बहन का गहरा स्नेह व सहयोग)। महत्वपूर्ण जीवन मोड़ों पर भाई-बहन आपके सबसे बड़े संबल बनेंगे।'
          : 'पारिवारिक दायित्वों अथवा संपत्ति को लेकर कभी-कभी मतभेद संभव। आर्थिक लेन-देन में पूर्ण पारदर्शिता रखें।',
        status: hasSiblingHarmony ? 'FAVORABLE' : 'DECISION_CRITICAL',
        timingWindow: resolveDynamicDashaWindow(kundali, 'Harmonious Bonding & Family Alignment'),
        confidenceScore: 88,
        karmicElasticity: 'ADRIDHA',
        karmicElasticityBadgeEn: 'KULA-DHARMA: RESTRAINT OVER PROPERTY CHATS FOSTERS PERMANENT BONDS',
        karmicElasticityBadgeHi: 'कुल-धर्म: संपत्ति चर्चा में संयम स्थायी प्रेम बनाए रखता है',
        strategicDirectionEn: 'Celebrate familial festivals together; never discuss partition disputes in front of aging parents.',
        strategicDirectionHi: 'त्योहारों पर परस्पर मिलन रखें; माता-पिता के सम्मुख संपत्ति विवाद की चर्चा न करें।',
        catalystEn: 'A balanced elder sibling or wise maternal uncle.',
        catalystHi: 'एक समझदार ज्येष्ठ भ्राता अथवा सुलझे हुए मामा।',
        trapdoorEn: 'Allowing in-laws or third-party gossip to poison fraternal trust.',
        trapdoorHi: 'तीसरे व्यक्ति अथवा बाहरी लोगों की बातों में आकर भाई से संदेह करना।',
        vyayaCostEn: 'Requires generosity with personal time during sibling family functions.',
        vyayaCostHi: 'भाई-बहन के पारिवारिक उत्सवों में समय व उदारता का समर्पण।',
        surgicalUpayaEn: 'Gift sweets or copper items to siblings on Raksha Bandhan/Bhai Dooj; offer water to Sun every Sunday.',
        surgicalUpayaHi: 'भाई दूज व रक्षाबंधन पर मीठा खिलाएं व तांबे का उपहार दें; सूर्य को नित्य तांबे के लोटे से अर्घ्य दें।',
        acharyaForensic: {
          activeDasha: `${dasha.maha} / ${dasha.antar}`,
          supportingTransits: 'Jupiter casting protective 9th/5th aspect on 3rd house of Sahodara',
          relevantHouses: [3, 11, 9],
          relevantVargas: ['D1', 'D3 Drekkana'],
          ashtakavargaBindus: h3,
          primaryPlanets: ['Mars', 'Jupiter', 'Mercury'],
          shlokaCitationEn: 'BPHS Ch. 12: When 3rd lord is associated with benefics, the native enjoys affectionate, brave, and prosperous siblings.',
          shlokaCitationHi: 'बृहत्पाराशर होराशास्त्र: तृतीयेश शुभ ग्रहों से युक्त हो तो जातक के भाई-बहन पराक्रमी, स्नेही व यशस्वी होते हैं।'
        }
      };
    }
  }
];

// ============================================================================
// 7. PUBLIC API ENGINE HELPERS
// ============================================================================

export function searchDeepQuestions(query: string): DeepQuestionDefinition[] {
  if (!query || query.trim() === '') {
    return DEEP_QUESTIONS_REGISTRY;
  }
  const q = query.toLowerCase().trim();
  return DEEP_QUESTIONS_REGISTRY.filter(item => {
    return (
      item.questionEn.toLowerCase().includes(q) ||
      item.questionHi.toLowerCase().includes(q) ||
      item.domainNameEn.toLowerCase().includes(q) ||
      item.domainNameHi.toLowerCase().includes(q) ||
      item.keywords.some(k => k.toLowerCase().includes(q))
    );
  });
}

export function evaluateDeepQuestion(
  questionId: string,
  kundali: CompleteKundali,
  lang: Language = 'en'
): DeepAnswerResult | null {
  const def = DEEP_QUESTIONS_REGISTRY.find(q => q.id === questionId);
  if (!def) return null;
  return def.evaluate(kundali, lang);
}

